import React, { useState, useEffect, useRef } from "react";
import {
  Volume2,
  Settings,
  BookOpen,
  X,
  Check,
  EyeOff,
  Eye,
  Mic,
  Brain,
  Home,
  Sparkles,
  RefreshCcw,
  Star,
  CheckSquare,
  ListOrdered,
  Square,
  Edit,
  PenTool,
  RefreshCw,
  Search,
  FolderPlus,
  Save,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Download,
  Upload,
  AlertTriangle,
  Cpu,
  ArrowRightLeft,
  Share,
  Lock,
  Unlock,
  KeyRound,
  Loader2,
  PlayCircle,
  Server,
  CloudLightning,
  Globe,
  ShoppingCart,
  Link,
  Wand2,
  RotateCcw,
  Ban,
  Archive,
  History,
  Filter,
  Headphones,
  Type,
  FileText,
  FileSpreadsheet,
  Printer,
  Languages,
  Smartphone,
} from "lucide-react";

// --- 音效资源 ---
const AUDIO_CORRECT =
  "data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodLb0oE8PHyBq6+wYC0sP2aJqK2jXjAuPWCJrK+nYzUwPmKIqq6mYzUwPmKIqq6mYzUwPmKIqq6mYzUwPmKIqq6mYzUwPmKIqq6mYzUwPmKIqq6mYzUwPmKIqq6mYzUwPmaJ";
const AUDIO_WRONG =
  "data:audio/wav;base64,UklGRiIGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodLb0oE8PHyBq6+wYC0sP2aJqK2jXjAuPWCJrK+nYzUwPmKIqq6mYzUwPmKIqq6mYzUwPmKIqq6mYzUwPmKIqq6mYzUwPmKIqq6mYzUwPmKIqq6mYzUwPmKIqq6mYzUwPmaJ";

// --- 初始数据 ---
const INITIAL_CARDS = [
  {
    id: "1",
    front: "Perseverance is key.",
    back: "坚持是关键",
    category: "默认",
    nextReview: Date.now(),
    lastReview: Date.now(),
    wrongCount: 0,
    interval: 0,
    repetition: 0,
    ef: 2.5,
    status: "new",
    isFavorite: false,
    isListeningFavorite: false,
  },
];

const playSound = (type) => {
  const audio = new Audio(type === "correct" ? AUDIO_CORRECT : AUDIO_WRONG);
  audio.volume = 0.5;
  audio.play().catch(() => {});
};

// --- 辅助工具 ---
const softNormalize = (text) => {
  if (!text) return "";
  let t = text.replace(/’|‘/g, "'").replace(/，/g, ",").replace(/。/g, ".");
  return t
    .toLowerCase()
    .trim()
    .replace(/[.,?!:;]$/, "")
    .replace(/\bwon't\b/g, "will not")
    .replace(/\bcan't\b/g, "cannot")
    .replace(/\bisn't\b/g, "is not")
    .replace(/\baren't\b/g, "are not")
    .replace(/\bn't\b/g, " not")
    .replace(/'m\b/g, " am")
    .replace(/'re\b/g, " are")
    .replace(/'ve\b/g, " have")
    .replace(/'d\b/g, " would")
    .replace(/'ll\b/g, " will")
    .replace(/'s\b/g, " is")
    .replace(/can not/g, "cannot");
};

const normalizeForDuplicateCheck = (text) => {
  return text
    .toLowerCase()
    .replace(/[.,?!:;()"']/g, "")
    .replace(/\s+/g, " ")
    .trim();
};

// --- 修改点：复习算法逻辑 ---
const isCardDue = (card) => {
  if (card.status === "deleted") return false;
  if (card.status === "new") return false;

  const now = Date.now();
  const last = card.lastReview || 0;
  const gap = now - last;

  if ((card.wrongCount || 0) > 0 && gap > 12 * 60 * 60 * 1000) return true;
  if (card.isFavorite && gap > 20 * 60 * 60 * 1000) return true;
  if (card.nextReview <= now) return true;
  if (gap > 14 * 86400000) return true;

  return false;
};

// --- 全局选词查词组件 ---
const TextSelectionHandler = () => {
  const [selection, setSelection] = useState(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    const handleSelection = () => {
      const sel = window.getSelection();
      const text = sel.toString().trim();

      if (text.length > 0) {
        try {
          const range = sel.getRangeAt(0);
          const rect = range.getBoundingClientRect();
          setPosition({
            top: rect.top + window.scrollY - 50,
            left: rect.left + window.scrollX + rect.width / 2,
          });
          setSelection(text);
        } catch (e) {
          setSelection(null);
        }
      } else {
        setSelection(null);
      }
    };

    document.addEventListener("mouseup", handleSelection);
    document.addEventListener("touchend", handleSelection);
    document.addEventListener("selectionchange", () => {
      if (!window.getSelection().toString().trim()) {
        setSelection(null);
      }
    });

    return () => {
      document.removeEventListener("mouseup", handleSelection);
      document.removeEventListener("touchend", handleSelection);
    };
  }, []);

  if (!selection) return null;

  const isPhrase = selection.split(/\s+/).length > 1;

  const handleLookup = (e) => {
    e.stopPropagation();
    let url;
    if (isPhrase) {
      url = `eudic://translate?text=${encodeURIComponent(selection)}`;
    } else {
      url = `eudic://dict/${encodeURIComponent(selection)}`;
    }
    window.location.href = url;
    window.getSelection().removeAllRanges();
    setSelection(null);
  };

  return (
    <div
      className="fixed z-[1000] transform -translate-x-1/2 animate-in fade-in zoom-in-95 duration-200"
      style={{ top: Math.max(10, position.top), left: position.left }}
    >
      <button
        onClick={handleLookup}
        className="flex items-center gap-2 bg-gray-900 text-white px-4 py-2 rounded-full shadow-xl font-bold text-sm active:scale-95 transition-transform"
      >
        {isPhrase ? <Languages size={16} /> : <Search size={16} />}
        {isPhrase ? "AI 翻译" : "欧路查词"}
      </button>
      <div className="w-3 h-3 bg-gray-900 rotate-45 absolute left-1/2 -translate-x-1/2 -bottom-1"></div>
    </div>
  );
};

// --- 基础组件 ---
const LoginView = ({
  inputPwd,
  setInputPwd,
  handleLogin,
  isUnlocking,
  loginError,
}) => (
  <div
    className={`fixed inset-0 z-50 bg-gray-100 flex flex-col items-center justify-center p-6 transition-opacity duration-500 ${
      isUnlocking ? "opacity-0 pointer-events-none" : "opacity-100"
    }`}
  >
    <div className="bg-white w-full max-w-sm rounded-3xl shadow-xl p-8 space-y-6 text-center animate-in zoom-in-95 duration-300">
      <div className="w-20 h-20 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mx-auto mb-2 shadow-inner">
        {isUnlocking ? <Unlock size={40} /> : <Lock size={40} />}
      </div>
      <div>
        <h1 className="text-2xl font-bold text-gray-800">SnapSent</h1>
        <p className="text-gray-500 text-sm mt-1">请输入访问密码</p>
      </div>
      <div className="relative group">
        <input
          type="password"
          inputMode="numeric"
          pattern="[0-9]*"
          autoFocus
          value={inputPwd}
          onChange={(e) => setInputPwd(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleLogin()}
          placeholder="Access Code"
          className="w-full pl-4 pr-4 py-4 bg-gray-50 border-2 border-gray-100 rounded-2xl outline-none focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-500/10 text-center font-bold text-xl tracking-widest transition-all"
        />
      </div>
      {loginError && (
        <div className="text-red-500 text-xs font-bold animate-pulse">
          密码错误，请重试
        </div>
      )}
      <button
        onClick={handleLogin}
        className="w-full py-4 bg-blue-600 text-white rounded-2xl font-bold text-lg shadow-lg shadow-blue-200 active:scale-95 transition-transform"
      >
        解锁进入
      </button>
    </div>
  </div>
);

const ClickableWord = ({ text, className }) => {
  const words = text.split(/(\s+)/);
  return (
    <div className={className}>
      {words.map((part, i) => {
        if (part.trim() === "") return <span key={i}>{part}</span>;
        const cleanWord = part.replace(/[.,?!:;()"']/g, "");
        return (
          <span
            key={i}
            className="cursor-pointer hover:text-blue-600 hover:underline active:text-blue-800 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              window.location.href = `eudic://dict/${cleanWord}`;
            }}
          >
            {part}
          </span>
        );
      })}
    </div>
  );
};

const DiffText = ({ input, target }) => {
  const inputWords = input.trim().split(/\s+/);
  const targetWords = target.trim().split(/\s+/);
  if (Math.abs(inputWords.length - targetWords.length) > 1) {
    return (
      <span className="underline decoration-black decoration-2">{input}</span>
    );
  }
  return (
    <span>
      {inputWords.map((word, idx) => {
        const currentWordNorm = softNormalize(word).replace(/[^a-z0-9]/g, "");
        const targetWordNorm = targetWords[idx]
          ? softNormalize(targetWords[idx]).replace(/[^a-z0-9]/g, "")
          : "";
        if (currentWordNorm === targetWordNorm) {
          return <span key={idx}>{word} </span>;
        }
        const targetOriginal = targetWords[idx] || "";
        let diffIndices = [];
        const maxLen = Math.max(word.length, targetOriginal.length);
        for (let i = 0; i < maxLen; i++) {
          if (word[i]?.toLowerCase() !== targetOriginal[i]?.toLowerCase()) {
            diffIndices.push(i);
          }
        }
        if (diffIndices.length >= 3 || !targetOriginal) {
          return (
            <span key={idx} className="underline decoration-black decoration-2">
              {word}{" "}
            </span>
          );
        }
        return (
          <span key={idx} className="mr-1">
            {word.split("").map((char, charIdx) => (
              <span
                key={charIdx}
                className={
                  diffIndices.includes(charIdx)
                    ? "underline decoration-black decoration-2"
                    : ""
                }
              >
                {char}
              </span>
            ))}{" "}
          </span>
        );
      })}
    </span>
  );
};

const HandwritingWidget = ({
  targetText,
  chineseText,
  cardId,
  onNext,
  onError,
  playAudio,
  autoFocus = true,
}) => {
  const [input, setInput] = useState("");
  const [status, setStatus] = useState("idle");
  const inputRef = useRef(null);
  useEffect(() => {
    setInput("");
    setStatus("idle");
  }, [cardId]);
  useEffect(() => {
    if (autoFocus && status === "idle") {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [cardId, autoFocus, status]);
  const normalizeForCheck = (text) => {
    let normalized = softNormalize(text);
    return normalized.replace(/[^a-z0-9]/g, "");
  };
  const handleCheck = () => {
    if (!input.trim()) return;
    if (normalizeForCheck(input) === normalizeForCheck(targetText)) {
      setStatus("correct");
      playSound("correct");
    } else {
      setStatus("error");
      playSound("wrong");
      if (onError) onError();
    }
  };
  const handleTryAgain = () => {
    setStatus("idle");
    setInput("");
    setTimeout(() => inputRef.current?.focus(), 100);
  };
  return (
    <div
      className="w-full mt-4 bg-gray-50 rounded-xl p-3 border border-gray-200 shadow-inner"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-gray-500 font-bold text-sm">
          <PenTool size={16} /> 随手练 (默写)
        </div>
        <button
          onClick={() => playAudio && playAudio(targetText)}
          className="p-2 bg-blue-100 text-blue-600 rounded-full active:scale-95"
        >
          <Volume2 size={18} />
        </button>
      </div>
      {chineseText && (
        <div className="mb-3 p-3 bg-white rounded-lg border border-dashed border-gray-200">
          <div className="text-xs text-gray-400 mb-1 font-bold">中文翻译：</div>
          <div className="text-lg text-gray-700 font-bold">{chineseText}</div>
        </div>
      )}
      {status === "correct" ? (
        <div className="flex flex-col items-center justify-center p-3 bg-green-50 rounded-lg border border-green-200 animate-in fade-in">
          <div className="flex items-center gap-2 text-green-600 font-bold text-md mb-2">
            <Check size={20} /> 回答正确！
          </div>
          <div className="text-green-800 font-medium mb-3 text-sm">
            {targetText}
          </div>
          <div className="flex gap-2 w-full">
            <button
              onClick={handleTryAgain}
              className="flex-1 py-1.5 bg-white text-green-600 border border-green-200 rounded-lg font-bold text-xs"
            >
              <RefreshCw size={14} className="inline mr-1" /> 再练一次
            </button>
            {onNext && (
              <button
                onClick={onNext}
                className="flex-1 py-1.5 bg-green-600 text-white rounded-lg font-bold text-xs"
              >
                下一个
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="relative">
            {status === "error" ? (
              <div className="w-full p-3 rounded-lg border-2 border-red-400 bg-red-50 text-red-500 line-through font-medium text-base min-h-[50px]">
                <DiffText input={input} target={targetText} />
              </div>
            ) : (
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleCheck();
                  }
                }}
                placeholder="在此输入英文..."
                className="w-full p-3 rounded-lg border-2 border-gray-300 focus:border-blue-500 bg-white outline-none font-medium text-base resize-none min-h-[50px] overflow-hidden"
                rows={1}
              />
            )}
            {status === "idle" && input.length > 0 && (
              <button
                onClick={handleCheck}
                className="absolute right-2 bottom-2 p-1.5 bg-blue-600 text-white rounded-md"
              >
                <Check size={18} />
              </button>
            )}
          </div>
          {status === "error" && (
            <div className="animate-in fade-in">
              <div className="p-3 bg-white rounded-lg border border-red-100 shadow-sm mb-3">
                <div className="text-xs text-gray-400 mb-1 font-bold">
                  正确答案：
                </div>
                <div className="text-lg text-gray-800 font-bold">
                  {targetText}
                </div>
              </div>
              <button
                onClick={handleTryAgain}
                className="w-full py-2 bg-gray-200 text-gray-700 rounded-lg font-bold flex items-center justify-center gap-2"
              >
                <RefreshCw size={16} /> 再次尝试
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const RecycleBinModal = ({ cards, setCards, onClose }) => {
  const deletedCards = cards.filter((c) => c.status === "deleted");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const handleToggle = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };
  const handleSelectAll = () => {
    if (selectedIds.size === deletedCards.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(deletedCards.map((c) => c.id)));
  };
  const handleRestore = () => {
    if (selectedIds.size === 0) return;
    setCards((prev) =>
      prev.map((c) =>
        selectedIds.has(c.id)
          ? { ...c, status: "new", nextReview: Date.now() }
          : c
      )
    );
    setSelectedIds(new Set());
    onClose();
  };
  const handlePermanentDelete = () => {
    if (
      !window.confirm(
        `确定永久删除这 ${selectedIds.size} 张卡片吗？此操作无法撤销。`
      )
    )
      return;
    setCards((prev) => prev.filter((c) => !selectedIds.has(c.id)));
    setSelectedIds(new Set());
    onClose();
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in">
      <div className="bg-white w-full max-md rounded-2xl p-0 shadow-2xl flex flex-col max-h-[80vh]">
        <div className="p-4 border-b flex justify-between items-center bg-gray-50 rounded-t-2xl">
          <h3 className="font-bold text-lg flex items-center gap-2">
            <Trash2 size={20} className="text-red-500" /> 回收站 (
            {deletedCards.length})
          </h3>
          <button
            onClick={onClose}
            className="p-2 bg-gray-200 rounded-full hover:bg-gray-300"
          >
            <X size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {deletedCards.length === 0 ? (
            <div className="text-center text-gray-400 py-10">
              回收站是空的。
            </div>
          ) : (
            deletedCards.map((c) => (
              <div
                key={c.id}
                onClick={() => handleToggle(c.id)}
                className={`p-3 rounded-lg border flex items-center gap-3 cursor-pointer ${
                  selectedIds.has(c.id)
                    ? "bg-blue-50 border-blue-500"
                    : "bg-white border-gray-200"
                }`}
              >
                <div
                  className={`w-5 h-5 rounded border flex items-center justify-center ${
                    selectedIds.has(c.id)
                      ? "bg-blue-500 border-blue-500 text-white"
                      : "border-gray-300"
                  }`}
                >
                  {selectedIds.has(c.id) && <Check size={14} />}
                </div>
                <div className="flex-1 truncate">
                  <div className="font-bold text-gray-800 text-sm truncate">
                    {c.front}
                  </div>
                  <div className="text-xs text-gray-500 truncate">{c.back}</div>
                </div>
              </div>
            ))
          )}
        </div>
        <div className="p-4 border-t bg-gray-50 rounded-b-2xl space-y-3">
          <div className="flex justify-between items-center text-sm">
            <button
              onClick={handleSelectAll}
              className="text-blue-600 font-bold"
            >
              {selectedIds.size === deletedCards.length &&
              deletedCards.length > 0
                ? "取消全选"
                : "全选"}
            </button>
            <span className="text-gray-400">已选: {selectedIds.size}</span>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleRestore}
              disabled={selectedIds.size === 0}
              className="flex-1 py-3 bg-green-600 text-white rounded-xl font-bold disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <RotateCcw size={16} /> 恢复
            </button>
            <button
              onClick={handlePermanentDelete}
              disabled={selectedIds.size === 0}
              className="flex-1 py-3 bg-red-600 text-white rounded-xl font-bold disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <Trash2 size={16} /> 彻底删除
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const EditModal = ({ card, onClose, onSave, settings }) => {
  const [f, setF] = useState(card.front);
  const [b, setB] = useState(card.back);
  const [isFixing, setIsFixing] = useState(false);
  const handleAutoFix = async () => {
    if (!f.trim()) return;
    setIsFixing(true);
    try {
      if (settings.openAIKey) {
        let baseUrl = settings.openAIBaseUrl || "https://api.openai.com/v1";
        if (baseUrl.endsWith("/")) baseUrl = baseUrl.slice(0, -1);
        if (!baseUrl.includes("/v1")) baseUrl = `${baseUrl}/v1`;
        const res = await fetch(`${baseUrl}/chat/completions`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${settings.openAIKey}`,
          },
          body: JSON.stringify({
            model: "gpt-3.5-turbo",
            messages: [
              {
                role: "system",
                content:
                  "You are a helpful editor. Correct the grammar, spelling, and capitalization of the user's input. Return ONLY the corrected English text, do not add any other words.",
              },
              { role: "user", content: f },
            ],
          }),
        });
        if (!res.ok) throw new Error("API Error");
        const data = await res.json();
        if (data.choices && data.choices[0]) {
          setF(data.choices[0].message.content.trim());
        }
      } else {
        let fixed = f.trim();
        fixed = fixed.charAt(0).toUpperCase() + fixed.slice(1);
        setF(fixed);
        alert(
          "已执行基础首字母大写修正。\n如需智能语法纠错，请在设置中配置 OpenAI Key。"
        );
      }
    } catch (e) {
      alert("纠错失败，请检查网络或 OpenAI 设置。");
    } finally {
      setIsFixing(false);
    }
  };
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in">
      <div className="bg-white w-full max-w-sm rounded-2xl p-6 shadow-2xl space-y-4">
        <h3 className="font-bold text-lg">✏️ 编辑内容</h3>
        <div className="relative">
          <label className="text-xs text-gray-400">Front (English)</label>
          <textarea
            value={f}
            onChange={(e) => setF(e.target.value)}
            className="w-full border rounded-lg p-2 pr-16"
            rows={3}
          />
          <button
            onClick={handleAutoFix}
            disabled={isFixing || !f}
            className="absolute right-2 top-6 text-[10px] bg-purple-50 text-purple-600 border border-purple-100 px-2 py-1 rounded hover:bg-purple-100 transition-colors flex items-center gap-1"
          >
            {isFixing ? (
              <Loader2 size={10} className="animate-spin" />
            ) : (
              <Wand2 size={10} />
            )}
            <span>纠错</span>
          </button>
        </div>
        <div className="relative">
          <label className="text-xs text-gray-400">Back (Chinese)</label>
          <textarea
            value={b}
            onChange={(e) => setB(e.target.value)}
            className="w-full border rounded-lg p-2"
            rows={3}
          />
          <button
            onClick={async () => {
              if (!f) return;
              try {
                const res = await fetch(
                  `https://api.mymemory.translated.net/get?q=${encodeURIComponent(
                    f
                  )}&langpair=en|zh`
                );
                const d = await res.json();
                if (d.responseData.translatedText)
                  setB(d.responseData.translatedText);
              } catch (e) {
                alert("翻译失败，请检查网络");
              }
            }}
            className="absolute right-2 top-6 text-[10px] bg-blue-50 text-blue-600 border border-blue-100 px-2 py-1 rounded hover:bg-blue-100 transition-colors"
          >
            Translate
          </button>
        </div>
        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 py-2 bg-gray-100 rounded-lg"
          >
            取消
          </button>
          <button
            onClick={() => {
              onSave(card.id, f, b);
              onClose();
            }}
            className="flex-1 py-2 bg-blue-600 text-white rounded-lg"
          >
            保存
          </button>
        </div>
      </div>
    </div>
  );
};

const CardListView = ({
  cards,
  viewMode,
  onBack,
  onSave,
  onDelete,
  onToggleFavorite,
  onHandwritingError,
  playAudio,
  isPlaying,
  settings,
}) => {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [selectedCat, setSelectedCat] = useState("全部");
  const [editCard, setEditCard] = useState(null);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const PAGE_SIZE = settings.browseGroupSize || 50;
  const scrollContainerRef = useRef(null);

  const activeCards = cards.filter((c) => c.status !== "deleted");
  const categories = ["全部", ...settings.categories];

  const filtered = activeCards
    .filter((c) => selectedCat === "全部" || c.category === selectedCat)
    .filter(
      (c) =>
        c.front.toLowerCase().includes(search.toLowerCase()) ||
        c.back.includes(search)
    )
    .sort((a, b) => Number(b.id) - Number(a.id));

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const currentData = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const toggleExpand = (id) => {
    setExpandedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);
      return newSet;
    });
  };

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [page, selectedCat]);

  let title = "";
  if (viewMode === "english") title = "英文浏览";
  else if (viewMode === "chinese") title = "中文浏览";
  else if (viewMode === "both") title = "中英双显";

  return (
    <div className="flex flex-col h-full bg-gray-50 animate-in fade-in overflow-hidden relative">
      <div className="flex-none bg-white p-4 border-b space-y-3 z-20 shadow-sm sticky top-0">
        <div className="flex justify-between items-center">
          <h2 className="font-bold text-xl">{title}</h2>
          <button onClick={onBack} className="p-2 bg-gray-100 rounded-full">
            <X size={20} />
          </button>
        </div>

        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => {
                setSelectedCat(cat);
                setPage(0);
              }}
              className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-colors flex-none ${
                selectedCat === cat
                  ? "bg-gray-800 text-white"
                  : "bg-gray-100 text-gray-500 border border-gray-200"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Search
              className="absolute left-3 top-2.5 text-gray-400"
              size={16}
            />
            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              className="w-full pl-9 pr-4 py-2 bg-gray-100 rounded-xl text-sm outline-none focus:ring-2 ring-blue-500"
              placeholder="在当前分类中搜索..."
            />
          </div>
        </div>
        {totalPages > 0 && (
          <div className="flex items-center gap-2 overflow-x-auto pb-1 no-scrollbar">
            <Filter size={16} className="text-gray-400 flex-none" />
            <select
              value={page}
              onChange={(e) => setPage(Number(e.target.value))}
              className="bg-gray-100 text-sm font-bold text-gray-700 py-1.5 px-3 rounded-lg border-none outline-none flex-1"
            >
              {Array.from({ length: totalPages }).map((_, idx) => {
                const start = idx * PAGE_SIZE + 1;
                const end = Math.min((idx + 1) * PAGE_SIZE, filtered.length);
                return (
                  <option key={idx} value={idx}>
                    Group {idx + 1} ({start}-{end})
                  </option>
                );
              })}
            </select>
            <div className="text-xs text-gray-400 whitespace-nowrap">
              共 {filtered.length} 条
            </div>
          </div>
        )}
      </div>
      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto p-4 space-y-3"
        style={{
          willChange: "scroll-position",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {currentData.map((c, i) => {
          const isExpanded = expandedIds.has(c.id);
          let showEnglish = false;
          let showChinese = false;
          if (viewMode === "english") {
            showEnglish = true;
            showChinese = isExpanded;
          } else if (viewMode === "chinese") {
            showChinese = true;
            showEnglish = isExpanded;
          } else if (viewMode === "both") {
            showEnglish = !isExpanded;
            showChinese = !isExpanded;
          }
          return (
            <div
              key={c.id}
              className="bg-white rounded-xl border flex flex-col shadow-sm relative overflow-hidden"
            >
              <div className="flex w-full">
                <div
                  onClick={(e) => {
                    e.preventDefault();
                    toggleExpand(c.id);
                  }}
                  className="flex-1 p-4 flex gap-3 cursor-pointer active:bg-gray-50 transition-colors select-none"
                >
                  <div className="text-xs text-gray-300 w-8 pt-1">
                    {page * PAGE_SIZE + i + 1}
                  </div>
                  <div className="flex-1 space-y-1">
                    {showEnglish && (
                      <ClickableWord
                        text={c.front}
                        className="text-xl font-bold text-gray-800 leading-tight"
                      />
                    )}
                    {showChinese && (
                      <div className="text-gray-500 mt-1">{c.back}</div>
                    )}
                    {viewMode === "english" && !showChinese && (
                      <div className="text-xs text-gray-300 italic pt-1">
                        点击显示中文
                      </div>
                    )}
                    {viewMode === "chinese" && !showEnglish && (
                      <div className="text-xs text-gray-300 italic pt-1">
                        点击显示英文
                      </div>
                    )}
                    <div className="text-[10px] text-gray-400 bg-gray-100 inline-block px-1.5 rounded mt-1">
                      {c.category}
                    </div>
                  </div>
                </div>
                <div className="flex flex-col justify-center border-l px-3 bg-white z-10 min-w-[50px]">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditCard(c);
                    }}
                    className="p-2 text-blue-500 hover:bg-blue-50 transition-colors"
                  >
                    <Edit size={18} />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      playAudio(c.front);
                    }}
                    className={`p-2 text-blue-500 rounded-full active:bg-blue-50 hover:bg-blue-50 transition-colors ${
                      isPlaying ? "opacity-50" : ""
                    }`}
                  >
                    <Volume2 size={18} />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => onToggleFavorite(c.id, e, "general")}
                    className={`p-2 ${
                      c.isFavorite ? "text-yellow-500" : "text-gray-300"
                    } hover:bg-yellow-50 transition-colors`}
                  >
                    <Star
                      size={18}
                      fill={c.isFavorite ? "currentColor" : "none"}
                    />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm("确定将此卡片移入回收站吗？"))
                        onDelete(c.id);
                    }}
                    className="p-2 text-red-400 hover:bg-red-50 transition-colors"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
              {viewMode === "chinese" && (
                <div className="bg-gray-50 px-4 pb-4 pt-1 border-t border-gray-100">
                  <HandwritingWidget
                    targetText={c.front}
                    chineseText={c.back}
                    cardId={c.id}
                    playAudio={playAudio}
                    autoFocus={false}
                    onError={() => onHandwritingError(c.id)}
                  />
                </div>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="text-center text-gray-400 py-10">无结果</div>
        )}
        <div className="h-10"></div>
      </div>
      {editCard && (
        <EditModal
          card={editCard}
          settings={settings}
          onClose={() => setEditCard(null)}
          onSave={onSave}
        />
      )}
    </div>
  );
};

const BrowseListView = ({
  browseMode,
  setBrowseMode,
  setActiveTab,
  cards,
  onSave,
  onDelete,
  onToggleFavorite,
  onHandwritingError,
  playAudio,
  isPlaying,
  settings,
}) => {
  if (browseMode) {
    return (
      <CardListView
        cards={cards}
        viewMode={browseMode}
        onBack={() => setBrowseMode(null)}
        onSave={onSave}
        onDelete={onDelete}
        onToggleFavorite={onToggleFavorite}
        onHandwritingError={onHandwritingError}
        playAudio={playAudio}
        isPlaying={isPlaying}
        settings={settings}
      />
    );
  }
  return (
    <div className="h-full overflow-y-auto p-6 space-y-6 pb-32 animate-in fade-in">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-800">卡片浏览模式</h2>
        <button
          onClick={() => setActiveTab("dashboard")}
          className="p-2 bg-gray-200 rounded-full"
        >
          <X />
        </button>
      </div>
      <div className="grid grid-cols-1 gap-4">
        <button
          onClick={() => setBrowseMode("english")}
          className="p-5 bg-blue-500 text-white rounded-2xl shadow-md active:scale-95 transition flex items-center gap-4 h-32"
        >
          <Volume2 size={32} className="bg-white/20 p-2 rounded-full" />
          <div className="text-left">
            <div className="font-bold text-lg">英文模块 (English First)</div>
            <div className="text-blue-100 text-sm">
              默认显示英文，点击显示中文
            </div>
          </div>
        </button>
        <button
          onClick={() => setBrowseMode("chinese")}
          className="p-5 bg-teal-500 text-white rounded-2xl shadow-md active:scale-95 transition flex items-center gap-4 h-32"
        >
          <BookOpen size={32} className="bg-white/20 p-2 rounded-full" />
          <div className="text-left">
            <div className="font-bold text-lg">中文模块 (Chinese First)</div>
            <div className="text-teal-100 text-sm">
              默认显示中文，下方提供随手练
            </div>
          </div>
        </button>
        <button
          onClick={() => setBrowseMode("both")}
          className="p-5 bg-purple-500 text-white rounded-2xl shadow-md active:scale-95 transition flex items-center gap-4 h-32"
        >
          <ArrowRightLeft size={32} className="bg-white/20 p-2 rounded-full" />
          <div className="text-left">
            <div className="font-bold text-lg">中英双显模块 (Both)</div>
            <div className="text-purple-100 text-sm">
              中英文同时显示，点击收起
            </div>
          </div>
        </button>
      </div>
    </div>
  );
};

const DashboardView = ({
  cards,
  startSession,
  setSelectionTarget,
  setActiveTab,
  hasSavedSession,
  resumeSession,
}) => {
  const activeCards = cards.filter((c) => c.status !== "deleted");
  const newCount = activeCards.filter((c) => c.status === "new").length;
  const dueCount = activeCards.filter((c) => isCardDue(c)).length;

  return (
    <div className="h-full overflow-y-auto p-6 space-y-6 pb-32 animate-in fade-in">
      <div className="mt-4">
        <h1 className="text-3xl font-bold text-gray-800">Hello, Learner!</h1>
        <p className="text-gray-500">SnapSent - 捕捉每一个灵感</p>
      </div>

      {hasSavedSession && (
        <button
          onClick={resumeSession}
          className="w-full p-4 bg-gray-800 text-white rounded-2xl shadow-lg flex items-center justify-between animate-pulse active:scale-95 transition-transform"
        >
          <div className="flex items-center gap-3">
            <History className="text-yellow-400" />
            <div className="text-left">
              <div className="font-bold">恢复上次进度</div>
              <div className="text-xs text-gray-400">点击继续未完成的学习</div>
            </div>
          </div>
          <ChevronRight />
        </button>
      )}

      <div className="grid gap-4">
        <div className="grid grid-cols-2 gap-4">
          <button
            onClick={() =>
              startSession(
                activeCards.filter((c) => c.status === "new"),
                "review"
              )
            }
            className="p-5 bg-blue-600 text-white rounded-3xl shadow-lg shadow-blue-200 active:scale-95 transition flex flex-col justify-between h-40"
          >
            <div className="bg-white/20 w-10 h-10 rounded-full flex items-center justify-center">
              <Sparkles />
            </div>
            <div>
              <div className="text-3xl font-bold">{newCount}</div>
              <div className="text-blue-100 text-sm">待学新词</div>
            </div>
          </button>
          <button
            onClick={() => setSelectionTarget("review_split")}
            className="p-5 bg-purple-600 text-white rounded-3xl shadow-lg shadow-purple-200 active:scale-95 transition flex flex-col justify-between h-40"
          >
            <div className="bg-white/20 w-10 h-10 rounded-full flex items-center justify-center">
              <Brain />
            </div>
            <div>
              <div className="text-3xl font-bold">{dueCount}</div>
              <div className="text-purple-100 text-sm">待复习</div>
            </div>
          </button>
        </div>
        <button
          onClick={() => setSelectionTarget("free_review")}
          className="p-5 bg-teal-500 text-white rounded-2xl shadow-md active:scale-95 transition flex items-center gap-4"
        >
          <div className="bg-white/20 p-3 rounded-full">
            <RefreshCcw />
          </div>
          <div className="flex-1">
            <div className="font-bold text-lg">自由复习</div>
            <div className="text-teal-100 text-sm">自定义范围学习</div>
          </div>
        </button>
        <button
          onClick={() => setSelectionTarget("speaking")}
          className="p-5 bg-orange-500 text-white rounded-2xl shadow-md active:scale-95 transition flex items-center gap-4"
        >
          <div className="bg-white/20 p-3 rounded-full">
            <Volume2 />
          </div>
          <div className="flex-1">
            <div className="font-bold text-lg">听音磨耳朵</div>
            <div className="text-orange-100 text-sm">纯听力与跟读模式</div>
          </div>
        </button>
        <button
          onClick={() => setActiveTab("browse")}
          className="p-5 bg-pink-500 text-white rounded-2xl shadow-md active:scale-95 transition flex items-center gap-4"
        >
          <div className="bg-white/20 p-3 rounded-full">
            <BookOpen />
          </div>
          <div className="flex-1">
            <div className="font-bold text-lg">卡片浏览</div>
            <div className="text-pink-100 text-sm">搜索、管理与查词</div>
          </div>
        </button>
      </div>
    </div>
  );
};

const SelectionView = ({
  cards,
  settings,
  selectionTarget,
  setSelectionTarget,
  startSession,
}) => {
  const [selectedCat, setSelectedCat] = useState("全部");
  const [mode, setMode] = useState("menu");
  const [manualSelection, setManualSelection] = useState(new Set());
  const [groupIndex, setGroupIndex] = useState(0);
  const [manualGroupIndex, setManualGroupIndex] = useState(0);

  const activeCards = cards.filter((c) => c.status !== "deleted");
  const categories = ["全部", ...settings.categories];

  const MenuBtn = ({ icon, title, sub, color, onClick }) => (
    <button
      onClick={onClick}
      className={`relative overflow-hidden p-4 rounded-2xl text-white shadow-md active:scale-95 transition-all flex flex-col justify-between h-28 ${color}`}
    >
      <div className="bg-white/20 w-10 h-10 rounded-full flex items-center justify-center mb-2">
        {icon}
      </div>
      <div>
        <div className="font-bold text-lg">{title}</div>
        <div className="text-xs opacity-80">{sub}</div>
      </div>
      <div className="absolute -right-4 -bottom-4 opacity-20 transform scale-150">
        {icon}
      </div>
    </button>
  );

  const getFilteredCards = () => {
    let filtered = [...activeCards].sort((a, b) => Number(b.id) - Number(a.id));
    if (selectedCat !== "全部")
      filtered = filtered.filter((c) => c.category === selectedCat);
    return filtered;
  };

  const handleQuickSelect = (type) => {
    const filtered = getFilteredCards();
    let queue = [];
    if (type === "new") {
      queue = filtered.filter(
        (c) => c.status === "new" || (c.repetition > 0 && c.repetition <= 3)
      );
    }
    if (type === "review") {
      queue = filtered.filter((c) => isCardDue(c));
    }
    if (type === "fav") {
      if (selectionTarget === "speaking") {
        queue = filtered.filter((c) => c.isListeningFavorite);
      } else {
        queue = filtered.filter((c) => c.isFavorite);
      }
    }
    startSession(queue, selectionTarget);
  };

  const handleManualSubmit = () => {
    const queue = getFilteredCards().filter((c) => manualSelection.has(c.id));
    startSession(queue, selectionTarget);
  };

  const handleGroupSubmit = () => {
    const sorted = getFilteredCards();
    const groupSize = settings.browseGroupSize || 50;
    const start = groupIndex * groupSize;
    const end = Math.min(sorted.length, start + groupSize);
    const queue = sorted.slice(start, end);
    startSession(queue, selectionTarget);
  };

  if (selectionTarget === "review_split") {
    const dueCards = activeCards.filter((c) => isCardDue(c));
    const speakingQueue = dueCards.filter((c) => c.isListeningFavorite);
    const writingQueue = dueCards.filter((c) => !c.isListeningFavorite);

    return (
      <div className="h-full p-6 space-y-6 animate-in slide-in-from-bottom-4 bg-gray-50">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold">待复习分类</h2>
          <button
            onClick={() => setSelectionTarget(null)}
            className="p-2 bg-gray-200 rounded-full"
          >
            <X />
          </button>
        </div>
        <div className="grid grid-cols-1 gap-4">
          <button
            onClick={() => startSession(speakingQueue, "speaking")}
            className="p-6 bg-indigo-600 text-white rounded-3xl shadow-xl flex items-center gap-4 active:scale-95 transition"
          >
            <div className="bg-white/20 p-4 rounded-full">
              <Headphones size={32} />
            </div>
            <div className="text-left">
              <div className="text-xl font-bold">
                听说模块 ({speakingQueue.length})
              </div>
              <div className="text-indigo-100 text-sm">听说不熟的句子</div>
            </div>
          </button>
          <button
            onClick={() => startSession(writingQueue, "review")}
            className="p-6 bg-rose-600 text-white rounded-3xl shadow-xl flex items-center gap-4 active:scale-95 transition"
          >
            <div className="bg-white/20 p-4 rounded-full">
              <Type size={32} />
            </div>
            <div className="text-left">
              <div className="text-xl font-bold">
                默写模块 ({writingQueue.length})
              </div>
              <div className="text-rose-100 text-sm">其他待复习句子</div>
            </div>
          </button>
        </div>
        <div className="text-center p-4 bg-blue-50 text-blue-600 rounded-2xl text-xs font-medium">
          💡 小贴士：算法会自动加入收藏、高频错题和太久没见的句子。
        </div>
      </div>
    );
  }

  if (mode === "manual") {
    const allFiltered = getFilteredCards();
    const groupSize = settings.browseGroupSize || 50;
    const totalGroups = Math.ceil(allFiltered.length / groupSize);
    const start = manualGroupIndex * groupSize;
    const currentGroupList = allFiltered.slice(start, start + groupSize);

    return (
      <div className="flex flex-col h-full bg-white animate-in slide-in-from-right">
        <div className="p-4 border-b flex justify-between items-center bg-gray-50">
          <div>
            <h3 className="font-bold">自由选择 ({manualSelection.size})</h3>
            <p className="text-[10px] text-gray-400">勾选完毕后点下方开始</p>
          </div>
          <button
            onClick={() => setMode("menu")}
            className="p-2 bg-gray-200 rounded-full"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-3 bg-gray-100 border-b flex items-center gap-2">
          <Filter size={14} className="text-gray-400" />
          <select
            value={manualGroupIndex}
            onChange={(e) => setManualGroupIndex(Number(e.target.value))}
            className="flex-1 bg-white text-sm font-bold p-2 rounded-lg outline-none border-none"
          >
            {Array.from({ length: totalGroups }).map((_, idx) => {
              const s = idx * groupSize + 1;
              const e = Math.min((idx + 1) * groupSize, allFiltered.length);
              return (
                <option key={idx} value={idx}>
                  Group {idx + 1} ({s}-{e})
                </option>
              );
            })}
          </select>
        </div>

        <div className="flex-1 overflow-y-auto p-2 space-y-2">
          {currentGroupList.length > 0 ? (
            currentGroupList.map((c) => (
              <div
                key={c.id}
                onClick={() => {
                  const newSet = new Set(manualSelection);
                  if (newSet.has(c.id)) newSet.delete(c.id);
                  else newSet.add(c.id);
                  setManualSelection(newSet);
                }}
                className={`p-3 rounded-lg border flex items-center gap-3 cursor-pointer transition-colors ${
                  manualSelection.has(c.id)
                    ? "bg-blue-50 border-blue-500"
                    : "bg-white"
                }`}
              >
                <div
                  className={`w-5 h-5 rounded border flex items-center justify-center ${
                    manualSelection.has(c.id)
                      ? "bg-blue-500 border-blue-500 text-white"
                      : "border-gray-300"
                  }`}
                >
                  {manualSelection.has(c.id) && <Check size={14} />}
                </div>
                <div className="truncate text-sm flex-1 font-medium">
                  {c.front}
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-10 text-gray-400">该组无内容</div>
          )}
        </div>
        <div className="p-4 border-t bg-gray-50">
          <button
            onClick={handleManualSubmit}
            disabled={manualSelection.size === 0}
            className="w-full py-3 bg-blue-600 text-white rounded-xl font-bold disabled:opacity-50 shadow-lg"
          >
            开始学习选中的 {manualSelection.size} 条
          </button>
        </div>
      </div>
    );
  }
  if (mode === "group") {
    const list = getFilteredCards();
    const groupSize = settings.browseGroupSize || 50;
    const totalGroups = Math.ceil(list.length / groupSize);
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-6 animate-in fade-in">
        <div className="bg-white w-full max-sm rounded-2xl p-6 space-y-6 text-center shadow-2xl">
          <h3 className="font-bold text-xl">分组选择</h3>
          <p className="text-gray-500 text-sm">
            当前列表共 {list.length} 条数据 (每组 {groupSize})
          </p>
          <div className="w-full">
            <select
              value={groupIndex}
              onChange={(e) => setGroupIndex(Number(e.target.value))}
              className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl font-bold text-gray-700 outline-none focus:ring-2 ring-blue-500"
            >
              {Array.from({ length: totalGroups }).map((_, idx) => {
                const start = idx * groupSize + 1;
                const end = Math.min((idx + 1) * groupSize, list.length);
                return (
                  <option key={idx} value={idx}>
                    Group {idx + 1} ({start}-{end})
                  </option>
                );
              })}
            </select>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setMode("menu")}
              className="flex-1 py-3 bg-gray-100 rounded-xl font-bold"
            >
              取消
            </button>
            <button
              onClick={handleGroupSubmit}
              disabled={list.length === 0}
              className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-bold disabled:opacity-50"
            >
              开始
            </button>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="h-full overflow-y-auto p-6 space-y-6 flex flex-col bg-gray-50 animate-in slide-in-from-bottom-4">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">
            {selectionTarget === "speaking" ? "听音磨耳朵" : "自由复习"}
          </h2>
          <p className="text-gray-500 text-sm">选择来源与分类</p>
        </div>
        <button
          onClick={() => setSelectionTarget(null)}
          className="p-2 bg-gray-200 rounded-full"
        >
          <X />
        </button>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() => {
              setSelectedCat(cat);
              setManualGroupIndex(0);
            }}
            className={`px-4 py-2 rounded-full text-sm font-bold whitespace-nowrap transition-colors ${
              selectedCat === cat
                ? "bg-gray-800 text-white"
                : "bg-white text-gray-600 border"
            }`}
          >
            {cat}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <MenuBtn
          icon={<Sparkles />}
          title="最近新学"
          sub="New & Recent"
          color="bg-blue-500"
          onClick={() => handleQuickSelect("new")}
        />
        <MenuBtn
          icon={<Brain />}
          title="复习"
          sub="Due Review"
          color="bg-purple-500"
          onClick={() => handleQuickSelect("review")}
        />
        <MenuBtn
          icon={<Star />}
          title="收藏"
          sub={
            selectionTarget === "speaking" ? "Listening Fav" : "Standard Fav"
          }
          color="bg-yellow-500"
          onClick={() => handleQuickSelect("fav")}
        />
        <MenuBtn
          icon={<CheckSquare />}
          title="自由选择"
          sub="Checkbox"
          color="bg-teal-500"
          onClick={() => setMode("manual")}
        />
        <MenuBtn
          icon={<ListOrdered />}
          title="分组选择"
          sub="Group Select"
          color="bg-pink-500"
          onClick={() => setMode("group")}
        />
      </div>
    </div>
  );
};

const AddCardView = ({
  settings,
  setSettings,
  cards,
  setCards,
  onInlineSave,
}) => {
  const [front, setFront] = useState("");
  const [back, setBack] = useState("");
  const [cat, setCat] = useState("默认");
  const [isManaging, setIsManaging] = useState(false);
  const [isFixing, setIsFixing] = useState(false);
  const [showRecycle, setShowRecycle] = useState(false);
  const [editCard, setEditCard] = useState(null);
  const importInputRef = useRef(null);

  const recentCards = cards
    .filter((c) => c.status !== "deleted")
    .sort((a, b) => Number(b.id) - Number(a.id))
    .slice(0, 10);

  const handleAutoFix = async () => {
    if (!front.trim()) return;
    setIsFixing(true);
    try {
      if (settings.openAIKey) {
        let baseUrl = settings.openAIBaseUrl || "https://api.openai.com/v1";
        if (baseUrl.endsWith("/")) baseUrl = baseUrl.slice(0, -1);
        if (!baseUrl.includes("/v1")) baseUrl = `${baseUrl}/v1`;
        const res = await fetch(`${baseUrl}/chat/completions`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${settings.openAIKey}`,
          },
          body: JSON.stringify({
            model: "gpt-3.5-turbo",
            messages: [
              {
                role: "system",
                content:
                  "You are a helpful editor. Correct the grammar, spelling, and capitalization of the user's input. Return ONLY the corrected English text, do not add any other words.",
              },
              { role: "user", content: front },
            ],
          }),
        });
        if (!res.ok) throw new Error("API Error");
        const data = await res.json();
        if (data.choices && data.choices[0]) {
          setFront(data.choices[0].message.content.trim());
        }
      } else {
        let fixed = front.trim();
        fixed = fixed.charAt(0).toUpperCase() + fixed.slice(1);
        setFront(fixed);
        alert(
          "已执行基础首字母大写修正。\n如需智能语法纠错，请配置 OpenAI Key。"
        );
      }
    } catch (e) {
      alert("纠错失败，请检查网络或 OpenAI 设置。");
    } finally {
      setIsFixing(false);
    }
  };

  const handleAdd = () => {
    const trimmedFront = front.trim();
    if (!trimmedFront) return;

    const normalizedNew = normalizeForDuplicateCheck(trimmedFront);
    const isDuplicate = cards.some(
      (c) =>
        c.status !== "deleted" &&
        normalizeForDuplicateCheck(c.front) === normalizedNew
    );

    if (isDuplicate) {
      alert("该句子已存在，请勿重复添加！");
      return;
    }

    const newCard = {
      id: Date.now().toString(),
      front: trimmedFront,
      back: back.trim() || "暂无翻译",
      category: cat,
      nextReview: Date.now(),
      lastReview: Date.now(),
      wrongCount: 0,
      status: "new",
      isFavorite: false,
      isListeningFavorite: false,
      interval: 0,
      repetition: 0,
      ef: 2.5,
    };
    setCards((prev) => [...prev, newCard]);
    setFront("");
    setBack("");
    const btn = document.getElementById("addBtn");
    if (btn) {
      const old = btn.innerText;
      btn.innerText = "已添加!";
      setTimeout(() => (btn.innerText = old), 1000);
    }
  };

  const handleImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const mode = window.confirm(
      "点击【确定】追加数据，点击【取消】清空旧数据？"
    )
      ? "append"
      : "replace";
    const targetCat = prompt("请输入分类名称", "导入数据");
    if (!targetCat) return;
    if (!settings.categories.includes(targetCat)) {
      setSettings((s) => ({ ...s, categories: [...s.categories, targetCat] }));
    }
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        let rows = evt.target.result.split("\n").map((line) => {
          let parts = line.split("\t");
          if (parts.length < 2) parts = line.split(/,|，/);
          return { f: parts[0]?.trim() || "", b: parts[1]?.trim() || "" };
        });

        const existingNorms = new Set(
          cards
            .filter((c) => c.status !== "deleted")
            .map((c) => normalizeForDuplicateCheck(c.front))
        );
        const seenInFile = new Set();

        const validNewRows = rows.filter((r) => {
          if (!r.f) return false;
          const norm = normalizeForDuplicateCheck(r.f);
          if (
            seenInFile.has(norm) ||
            (mode === "append" && existingNorms.has(norm))
          )
            return false;
          seenInFile.add(norm);
          return true;
        });

        const newCards = validNewRows.map((r) => ({
          id: Date.now().toString() + Math.random().toString().slice(2, 6),
          front: r.f,
          back: r.b || "",
          category: targetCat,
          nextReview: Date.now(),
          lastReview: Date.now(),
          wrongCount: 0,
          status: "new",
          isFavorite: false,
          isListeningFavorite: false,
          interval: 0,
          repetition: 0,
          ef: 2.5,
        }));

        if (newCards.length === 0) {
          alert("未识别到新数据。");
          return;
        }
        if (mode === "replace") setCards(newCards);
        else setCards((prev) => [...prev, ...newCards]);
        alert(`成功导入 ${newCards.length} 条数据`);
      } catch (err) {
        alert("文件解析失败");
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  return (
    <div className="p-4 flex flex-col h-full bg-gray-50 pb-20 overflow-y-auto">
      <div className="flex justify-between items-center mb-2 flex-none">
        <h2 className="text-2xl font-bold text-gray-800">添加卡片</h2>
        <button
          onClick={() => setIsManaging(true)}
          className="text-xs text-blue-600 font-bold bg-blue-50 px-3 py-1 rounded-full"
        >
          管理分类
        </button>
      </div>
      <div className="space-y-3 flex-none">
        <div>
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
            {settings.categories.map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                className={`px-3 py-1 rounded-full text-xs font-bold border transition-colors ${
                  cat === c
                    ? "bg-gray-800 text-white"
                    : "bg-white text-gray-500"
                }`}
              >
                {c}
              </button>
            ))}
            <button
              onClick={() => {
                const n = prompt("新分类名称:");
                if (n && !settings.categories.includes(n)) {
                  setSettings((s) => ({
                    ...s,
                    categories: [...s.categories, n],
                  }));
                  setCat(n);
                }
              }}
              className="px-3 py-1 rounded-full text-xs bg-gray-200 text-gray-600"
            >
              +
            </button>
          </div>
        </div>
        <div className="relative">
          <textarea
            value={front}
            onChange={(e) => setFront(e.target.value)}
            placeholder="English sentence..."
            className="w-full p-3 border rounded-xl outline-none focus:ring-2 ring-blue-500 font-medium pr-20"
            rows={3}
          />
          <button
            onClick={handleAutoFix}
            disabled={isFixing || !front}
            className="absolute right-2 top-2 text-[10px] bg-purple-50 text-purple-600 border border-purple-100 px-2 py-1 rounded-lg hover:bg-purple-100 transition-colors flex items-center gap-1"
          >
            {isFixing ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <Wand2 size={12} />
            )}
            <span>纠错</span>
          </button>
        </div>
        <div className="relative">
          <textarea
            value={back}
            onChange={(e) => setBack(e.target.value)}
            placeholder="中文翻译..."
            className="w-full p-3 border rounded-xl outline-none focus:ring-2 ring-blue-500"
            rows={2}
          />
          <button
            onClick={async () => {
              if (!front) return;
              const res = await fetch(
                `https://api.mymemory.translated.net/get?q=${encodeURIComponent(
                  front
                )}&langpair=en|zh`
              );
              const d = await res.json();
              if (d.responseData.translatedText)
                setBack(d.responseData.translatedText);
            }}
            className="absolute right-2 bottom-2 text-[10px] bg-gray-100 px-2 py-1 rounded text-gray-500"
          >
            Translate
          </button>
        </div>
        <button
          id="addBtn"
          onClick={handleAdd}
          className="w-full bg-blue-600 text-white py-3 rounded-xl font-bold shadow-md active:scale-95 transition-transform"
        >
          确认添加
        </button>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => importInputRef.current.click()}
            className="py-2 bg-green-50 text-green-700 font-bold rounded-lg text-xs flex items-center justify-center gap-2"
          >
            <FolderPlus size={14} /> 批量导入
          </button>
          <button
            onClick={() => setShowRecycle(true)}
            className="py-2 bg-gray-100 text-gray-700 font-bold rounded-lg text-xs flex items-center justify-center gap-2"
          >
            <Archive size={14} /> 回收站 (
            {cards.filter((c) => c.status === "deleted").length})
          </button>
        </div>
        <input
          type="file"
          ref={importInputRef}
          onChange={handleImport}
          hidden
          accept=".txt,.csv"
        />
      </div>

      <div className="mt-6 flex-1">
        <div className="flex items-center gap-2 text-gray-400 mb-3 px-1">
          <History size={16} />
          <span className="text-sm font-bold uppercase tracking-wider">
            最近添加
          </span>
        </div>
        <div className="space-y-2">
          {recentCards.map((c) => (
            <div
              key={c.id}
              className="bg-white p-3 rounded-xl border border-gray-100 flex items-center justify-between shadow-sm animate-in fade-in slide-in-from-left-2 duration-300"
            >
              <div className="flex-1 truncate mr-4">
                <div className="text-sm font-bold text-gray-800 truncate">
                  {c.front}
                </div>
                <div className="text-[10px] text-gray-400 truncate">
                  {c.back}
                </div>
              </div>
              <button
                onClick={() => setEditCard(c)}
                className="p-2 bg-blue-50 text-blue-500 rounded-lg active:scale-90 transition-transform"
              >
                <Edit size={16} />
              </button>
            </div>
          ))}
          {recentCards.length === 0 && (
            <div className="text-center py-8 text-gray-300 text-sm italic">
              暂无最近记录
            </div>
          )}
        </div>
      </div>

      {showRecycle && (
        <RecycleBinModal
          cards={cards}
          setCards={setCards}
          onClose={() => setShowRecycle(false)}
        />
      )}
      {isManaging && (
        <div className="fixed inset-0 z-50 bg-gray-50 p-6 flex flex-col">
          <button
            onClick={() => setIsManaging(false)}
            className="mb-4 text-sm text-gray-500 flex items-center"
          >
            <ChevronLeft size={16} /> 返回
          </button>
          <h2 className="font-bold text-xl mb-4">分类管理</h2>
          <div className="space-y-2 flex-1 overflow-y-auto">
            {settings.categories.map((c) => (
              <div
                key={c}
                className="bg-white p-3 rounded-lg border flex justify-between items-center"
              >
                <span className="font-medium">{c}</span>
                <div className="text-xs text-gray-400">
                  (
                  {
                    cards.filter(
                      (card) => card.category === c && card.status !== "deleted"
                    ).length
                  }{" "}
                  cards)
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {editCard && (
        <EditModal
          card={editCard}
          settings={settings}
          onClose={() => setEditCard(null)}
          onSave={onInlineSave}
        />
      )}
    </div>
  );
};

const SentenceExportModal = ({ cards, settings, onClose }) => {
  const [range, setRange] = useState("all");
  const [selectedCat, setSelectedCat] = useState("全部");
  const [format, setFormat] = useState("txt");
  const [selection, setSelection] = useState(new Set());

  const activeCards = cards.filter((c) => c.status !== "deleted");
  const categories = ["全部", ...settings.categories];

  const getExportData = () => {
    let list = [...activeCards].sort((a, b) => Number(b.id) - Number(a.id));
    if (range === "recent") list = list.slice(0, 50);
    if (range === "selective") {
      if (selectedCat !== "全部")
        list = list.filter((c) => c.category === selectedCat);
      if (selection.size > 0) list = list.filter((c) => selection.has(c.id));
    }
    return list;
  };

  const handleExport = () => {
    const data = getExportData();
    if (data.length === 0) return alert("没有可导出的数据");

    if (format === "txt") {
      const content = data.map((c) => `${c.front}\n${c.back}\n`).join("\n");
      downloadFile(content, "SnapSent_Export.txt", "text/plain");
    } else if (format === "csv") {
      const csvHeader = "English,Chinese,Category\n";
      const csvRows = data
        .map((c) => {
          const f = c.front ? c.front.replace(/"/g, '""') : "";
          const b = c.back ? c.back.replace(/"/g, '""') : "";
          const cat = c.category ? c.category.replace(/"/g, '""') : "";
          return `"${f}","${b}","${cat}"`;
        })
        .join("\n");

      const blob = new Blob(["\uFEFF", csvHeader + csvRows], {
        type: "text/csv;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "SnapSent_Export.csv";
      a.click();
    } else if (format === "print") {
      const printWindow = window.open("", "_blank");
      const html = `
        <html><head><title>SnapSent Export</title><style>
          body{font-family:sans-serif;padding:20px;}
          .item{margin-bottom:20px;padding-bottom:10px;border-bottom:1px solid #eee;}
          .en{font-size:18px;font-weight:bold;}
          .zh{color:#666;margin-top:4px;}
        </style></head><body>
          <h2>SnapSent 导出内容 (${data.length}条)</h2>
          ${data
            .map(
              (c) =>
                `<div class="item"><div class="en">${c.front}</div><div class="zh">${c.back}</div></div>`
            )
            .join("")}
          <script>window.print();</script>
        </body></html>
      `;
      printWindow.document.write(html);
      printWindow.document.close();
    }
    onClose();
  };

  const downloadFile = (content, name, type) => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in">
      <div className="bg-white w-full max-w-sm rounded-3xl shadow-2xl flex flex-col max-h-[90vh]">
        <div className="p-5 border-b flex justify-between items-center">
          <h3 className="font-bold text-lg flex items-center gap-2">
            <Download size={20} className="text-blue-600" /> 句子导出
          </h3>
          <button onClick={onClose} className="p-2 bg-gray-100 rounded-full">
            <X size={16} />
          </button>
        </div>

        <div className="p-5 overflow-y-auto space-y-6">
          <section className="space-y-3">
            <div className="text-xs font-bold text-gray-400 uppercase tracking-widest">
              第一步：选择范围
            </div>
            <div className="grid grid-cols-1 gap-2">
              {[
                {
                  id: "all",
                  label: "全部导出",
                  sub: `所有 ${activeCards.length} 条记录`,
                },
                { id: "recent", label: "新添加导出", sub: "最近添加的 50 条" },
                {
                  id: "selective",
                  label: "选择导出",
                  sub: "按分类筛选并手动打勾",
                },
              ].map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => setRange(opt.id)}
                  className={`p-3 rounded-xl border-2 text-left transition-all ${
                    range === opt.id
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-100 bg-gray-50"
                  }`}
                >
                  <div className="font-bold text-sm">{opt.label}</div>
                  <div className="text-[10px] text-gray-400">{opt.sub}</div>
                </button>
              ))}
            </div>
            {range === "selective" && (
              <div className="pt-2 animate-in slide-in-from-top-2">
                <select
                  value={selectedCat}
                  onChange={(e) => setSelectedCat(e.target.value)}
                  className="w-full p-2 bg-white border rounded-lg text-sm font-bold outline-none"
                >
                  {categories.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </section>

          <section className="space-y-3">
            <div className="text-xs font-bold text-gray-400 uppercase tracking-widest">
              第二步：选择格式
            </div>
            <div className="flex gap-2">
              {[
                { id: "txt", icon: <FileText size={16} />, label: "TXT" },
                {
                  id: "csv",
                  icon: <FileSpreadsheet size={16} />,
                  label: "Excel",
                },
                { id: "print", icon: <Printer size={16} />, label: "PDF/打印" },
              ].map((f) => (
                <button
                  key={f.id}
                  onClick={() => setFormat(f.id)}
                  className={`flex-1 py-3 rounded-xl border-2 flex flex-col items-center gap-1 transition-all ${
                    format === f.id
                      ? "border-blue-500 bg-blue-50 text-blue-600"
                      : "border-gray-100 bg-white text-gray-400"
                  }`}
                >
                  {f.icon}
                  <span className="text-xs font-bold">{f.label}</span>
                </button>
              ))}
            </div>
          </section>
        </div>

        <div className="p-5 border-t bg-gray-50 rounded-b-3xl">
          <button
            onClick={handleExport}
            className="w-full py-4 bg-blue-600 text-white rounded-2xl font-bold shadow-lg shadow-blue-100 active:scale-95 transition-transform"
          >
            确认并开始导出
          </button>
        </div>
      </div>
    </div>
  );
};

const SettingsView = ({
  settings,
  setSettings,
  cards,
  setCards,
  playAudio,
}) => {
  const fileInputRef = useRef(null);
  const [showExport, setShowExport] = useState(false);
  const [localGroupId, setLocalGroupId] = useState(settings.minimaxGroupId);
  const [localApiKey, setLocalApiKey] = useState(settings.minimaxApiKey);
  const [localOpenAIKey, setLocalOpenAIKey] = useState(settings.openAIKey);
  const [localOpenAIBaseUrl, setLocalOpenAIBaseUrl] = useState(
    settings.openAIBaseUrl
  );
  const [localElevenLabsKey, setLocalElevenLabsKey] = useState(
    settings.elevenLabsApiKey
  );
  const [localGoogleKey, setLocalGoogleKey] = useState(settings.googleApiKey);

  // --- 修改点：新增各种BaseURL的本地状态 ---
  const [localMinimaxBaseUrl, setLocalMinimaxBaseUrl] = useState(
    settings.minimaxBaseUrl
  );
  const [localElevenLabsBaseUrl, setLocalElevenLabsBaseUrl] = useState(
    settings.elevenLabsBaseUrl
  );
  const [localGoogleBaseUrl, setLocalGoogleBaseUrl] = useState(
    settings.googleBaseUrl
  );

  useEffect(() => {
    setLocalGroupId(settings.minimaxGroupId);
    setLocalApiKey(settings.minimaxApiKey);
    setLocalOpenAIKey(settings.openAIKey);
    setLocalOpenAIBaseUrl(
      settings.openAIBaseUrl || "https://api.openai.com/v1"
    );
    setLocalElevenLabsKey(settings.elevenLabsApiKey);
    setLocalGoogleKey(settings.googleApiKey);

    // --- 同步初始化BaseURL ---
    setLocalMinimaxBaseUrl(
      settings.minimaxBaseUrl || "https://api.minimax.chat/v1/t2a_v2"
    );
    setLocalElevenLabsBaseUrl(
      settings.elevenLabsBaseUrl ||
        "https://api.elevenlabs.io/v1/text-to-speech"
    );
    setLocalGoogleBaseUrl(
      settings.googleBaseUrl ||
        "https://texttospeech.googleapis.com/v1/text:synthesize"
    );
  }, [settings]);

  const handleExportBackup = async () => {
    const name = `SnapSentBackup-${new Date().toISOString().slice(0, 10)}.json`;
    const data = JSON.stringify({ cards, settings });
    const blob = new Blob([data], { type: "application/json" });
    const file = new File([blob], name, { type: "application/json" });
    if (
      navigator.share &&
      navigator.canShare &&
      navigator.canShare({ files: [file] })
    ) {
      try {
        await navigator.share({ files: [file], title: "SnapSent Backup" });
        setSettings((s) => ({ ...s, lastBackupDate: Date.now() }));
        return;
      } catch (err) {}
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();
    setSettings((s) => ({ ...s, lastBackupDate: Date.now() }));
  };

  const handleRestore = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const d = JSON.parse(evt.target.result);
        if (d.cards && window.confirm(`恢复将覆盖当前数据，确定？`)) {
          setCards(d.cards);
          if (d.settings) setSettings(d.settings);
          alert("恢复成功！");
        }
      } catch (err) {
        alert("文件无效");
      }
    };
    reader.readAsText(file);
  };

  return (
    <div className="p-6 space-y-6 h-full overflow-y-auto pb-40">
      <h2 className="text-2xl font-bold text-gray-800">设置</h2>

      <div className="bg-yellow-50 p-4 rounded-2xl border border-yellow-200 text-sm text-yellow-800 space-y-2">
        <div className="font-bold flex items-center gap-2">
          <AlertTriangle size={18} /> 重要提示：更换浏览器
        </div>
        <p>
          因手机系统安全限制，Safari 和 Chrome
          的数据互不相通。如需更换浏览器，请先在旧浏览器中点击“备份数据”，然后在积浏览器中点击“恢复数据”。
        </p>
      </div>

      <div className="bg-white p-5 rounded-2xl border shadow-sm space-y-4">
        <h3 className="font-bold text-gray-700 flex items-center gap-2">
          <Lock size={18} /> 安全与备份
        </h3>
        <div>
          <label className="text-xs text-gray-500 font-bold">
            App 启动密码
          </label>
          <input
            type="text"
            value={settings.accessPassword}
            onChange={(e) =>
              setSettings((s) => ({ ...s, accessPassword: e.target.value }))
            }
            className="w-full p-3 mt-1 border rounded-xl font-bold tracking-widest text-center bg-gray-50 focus:bg-white transition-colors outline-none"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={handleExportBackup}
            className="py-3 bg-blue-50 text-blue-600 font-bold rounded-lg text-xs flex flex-col items-center gap-1"
          >
            <Share size={16} /> 备份数据 (导出)
          </button>
          <button
            onClick={() => fileInputRef.current.click()}
            className="py-3 bg-green-50 text-green-600 font-bold rounded-lg text-xs flex flex-col items-center gap-1"
          >
            <Upload size={16} /> 恢复数据 (导入)
          </button>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleRestore}
            hidden
            accept=".json"
          />
        </div>
      </div>

      <div className="bg-white p-5 rounded-2xl border shadow-sm space-y-4">
        <h3 className="font-bold text-gray-700 flex items-center gap-2">
          <Settings size={18} /> 常规设置
        </h3>
        <div className="flex justify-between items-center">
          <span className="text-gray-800">自动朗读</span>
          <input
            type="checkbox"
            checked={settings.autoPlayAudio}
            onChange={() =>
              setSettings((s) => ({ ...s, autoPlayAudio: !s.autoPlayAudio }))
            }
            className="w-5 h-5"
          />
        </div>
        <div className="flex justify-between items-center">
          <span className="text-gray-800">反转正反面 (中→英)</span>
          <input
            type="checkbox"
            checked={settings.swapSides}
            onChange={() =>
              setSettings((s) => ({ ...s, swapSides: !s.swapSides }))
            }
            className="w-5 h-5"
          />
        </div>
        <div className="flex justify-between items-center">
          <span>单次复习数量</span>
          <input
            type="number"
            value={settings.reviewLimit}
            onChange={(e) =>
              setSettings((s) => ({
                ...s,
                reviewLimit: Number(e.target.value),
              }))
            }
            className="w-16 border rounded text-center"
          />
        </div>
        <div className="flex justify-between items-center">
          <span>浏览页每组数量</span>
          <input
            type="number"
            value={settings.browseGroupSize || 50}
            onChange={(e) =>
              setSettings((s) => ({
                ...s,
                browseGroupSize: Number(e.target.value),
              }))
            }
            className="w-16 border rounded text-center"
          />
        </div>
      </div>

      <div className="bg-gradient-to-br from-blue-600 to-indigo-700 p-5 rounded-3xl shadow-lg text-white space-y-4">
        <div className="flex items-center gap-3">
          <div className="bg-white/20 p-3 rounded-2xl">
            <Download size={24} />
          </div>
          <div>
            <div className="font-bold text-lg">句子批量导出</div>
            <div className="text-blue-100 text-[10px] opacity-80">
              导出为 TXT, Excel 或 PDF
            </div>
          </div>
        </div>
        <button
          onClick={() => setShowExport(true)}
          className="w-full py-3 bg-white text-blue-600 rounded-xl font-bold shadow-sm active:scale-95 transition-transform"
        >
          立即开始导出
        </button>
      </div>

      <div className="bg-white p-5 rounded-2xl border shadow-sm space-y-4">
        <div className="flex items-center gap-2 text-indigo-600 font-bold">
          <Cpu size={18} /> AI 语音服务商
        </div>
        <div className="flex flex-wrap gap-2 text-sm font-bold">
          {[
            { id: "browser", label: "Browser" },
            { id: "elevenlabs", label: "ElevenLabs" },
            { id: "google", label: "Google" },
            { id: "openai", label: "OpenAI" },
            { id: "minimax", label: "MiniMax" },
          ].map((opt) => (
            <button
              key={opt.id}
              onClick={() =>
                setSettings((s) => ({ ...s, ttsProvider: opt.id }))
              }
              className={`px-3 py-1.5 rounded-lg border transition-colors ${
                settings.ttsProvider === opt.id
                  ? "bg-indigo-600 text-white border-indigo-600 shadow-md"
                  : "bg-gray-50 text-gray-600 border-gray-200"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        {settings.ttsProvider === "elevenlabs" && (
          <div className="space-y-3 animate-in fade-in bg-indigo-50 p-4 rounded-xl">
            <div className="flex items-center gap-2 font-bold text-indigo-700">
              <CloudLightning size={16} /> ElevenLabs 设置
            </div>
            {/* --- 修改点：增加 ElevenLabs Base URL 输入 --- */}
            <div>
              <label className="text-xs text-gray-500 font-bold">
                接口地址 (Base URL)
              </label>
              <input
                type="text"
                value={localElevenLabsBaseUrl}
                onChange={(e) => setLocalElevenLabsBaseUrl(e.target.value)}
                onBlur={() =>
                  setSettings((s) => ({
                    ...s,
                    elevenLabsBaseUrl: localElevenLabsBaseUrl,
                  }))
                }
                placeholder="https://api.elevenlabs.io/v1/text-to-speech"
                className="w-full p-2 mt-1 border rounded-lg bg-white font-mono text-xs"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 font-bold">API Key</label>
              <input
                type="password"
                value={localElevenLabsKey}
                onChange={(e) => setLocalElevenLabsKey(e.target.value)}
                onBlur={() =>
                  setSettings((s) => ({
                    ...s,
                    elevenLabsApiKey: localElevenLabsKey,
                  }))
                }
                placeholder="xi-api-key"
                className="w-full p-2 mt-1 border rounded-lg bg-white"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 font-bold">
                Voice ID
              </label>
              <input
                type="text"
                value={settings.elevenLabsVoiceId}
                onChange={(e) =>
                  setSettings((s) => ({
                    ...s,
                    elevenLabsVoiceId: e.target.value,
                  }))
                }
                placeholder="21m00Tcm4TlvDq8ikWAM"
                className="w-full p-2 mt-1 border rounded-lg bg-white font-mono text-xs"
              />
            </div>
            <button
              onClick={() => playAudio("ElevenLabs is working.")}
              className="w-full py-2 bg-indigo-600 text-white rounded-lg font-bold text-xs flex items-center justify-center gap-2"
            >
              <PlayCircle size={14} /> 测试 ElevenLabs
            </button>
          </div>
        )}
        {settings.ttsProvider === "google" && (
          <div className="space-y-3 animate-in fade-in bg-blue-50 p-4 rounded-xl">
            <div className="flex items-center gap-2 font-bold text-blue-700">
              <Globe size={16} /> Google TTS 设置
            </div>
            {/* --- 修改点：增加 Google Base URL 输入 --- */}
            <div>
              <label className="text-xs text-gray-500 font-bold">
                接口地址 (Base URL)
              </label>
              <input
                type="text"
                value={localGoogleBaseUrl}
                onChange={(e) => setLocalGoogleBaseUrl(e.target.value)}
                onBlur={() =>
                  setSettings((s) => ({
                    ...s,
                    googleBaseUrl: localGoogleBaseUrl,
                  }))
                }
                placeholder="https://texttospeech.googleapis.com/v1/text:synthesize"
                className="w-full p-2 mt-1 border rounded-lg bg-white font-mono text-xs"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 font-bold">API Key</label>
              <input
                type="password"
                value={localGoogleKey}
                onChange={(e) => setLocalGoogleKey(e.target.value)}
                onBlur={() =>
                  setSettings((s) => ({ ...s, googleApiKey: localGoogleKey }))
                }
                placeholder="AIzaSy..."
                className="w-full p-2 mt-1 border rounded-lg bg-white"
              />
            </div>
            <button
              onClick={() => playAudio("Google TTS is working.")}
              className="w-full py-2 bg-blue-600 text-white rounded-lg font-bold text-xs flex items-center justify-center gap-2"
            >
              <PlayCircle size={14} /> 测试 Google TTS
            </button>
          </div>
        )}
        {settings.ttsProvider === "openai" && (
          <div className="space-y-3 animate-in fade-in bg-gray-50 p-4 rounded-xl">
            <div className="flex items-center gap-2 font-bold text-gray-700">
              <Link size={16} /> OpenAI 设置
            </div>
            {/* --- OpenAI Base URL 修改点 --- */}
            <div>
              <label className="text-xs text-gray-500 font-bold">
                接口地址 (Base URL)
              </label>
              <input
                type="text"
                value={localOpenAIBaseUrl}
                onChange={(e) => setLocalOpenAIBaseUrl(e.target.value)}
                onBlur={() =>
                  setSettings((s) => ({
                    ...s,
                    openAIBaseUrl: localOpenAIBaseUrl,
                  }))
                }
                placeholder="https://api.openai.com/v1"
                className="w-full p-2 border rounded-lg font-mono text-sm mt-1"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 font-bold">API Key</label>
              <input
                type="password"
                value={localOpenAIKey}
                onChange={(e) => setLocalOpenAIKey(e.target.value)}
                onBlur={() =>
                  setSettings((s) => ({ ...s, openAIKey: localOpenAIKey }))
                }
                placeholder="sk-..."
                className="w-full p-2 border rounded-lg font-mono text-sm mt-1"
              />
            </div>
            <button
              onClick={() => playAudio("OpenAI speech is working.")}
              className="w-full mt-2 py-2 bg-black text-white text-xs font-bold rounded-lg flex items-center justify-center gap-2"
            >
              <PlayCircle size={14} /> 测试语音
            </button>
          </div>
        )}
        {settings.ttsProvider === "minimax" && (
          <div className="space-y-3 animate-in fade-in bg-orange-50 p-4 rounded-xl">
            <div className="flex items-center gap-2 font-bold text-orange-700">
              <Link size={16} /> MiniMax T2A V2
            </div>
            {/* --- 修改点：增加 MiniMax Base URL 输入 --- */}
            <div>
              <label className="text-xs text-gray-500 font-bold">
                接口地址 (Base URL)
              </label>
              <input
                type="text"
                value={localMinimaxBaseUrl}
                onChange={(e) => setLocalMinimaxBaseUrl(e.target.value)}
                onBlur={() =>
                  setSettings((s) => ({
                    ...s,
                    minimaxBaseUrl: localMinimaxBaseUrl,
                  }))
                }
                placeholder="https://api.minimax.chat/v1/t2a_v2"
                className="w-full p-2 border rounded-lg font-mono text-sm mt-1"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 font-bold">模型</label>
              <select
                value={settings.minimaxModel}
                onChange={(e) =>
                  setSettings((s) => ({ ...s, minimaxModel: e.target.value }))
                }
                className="w-full p-2 mt-1 border rounded-lg bg-white text-sm"
              >
                <option value="speech-02-hd">speech-02-hd</option>
                <option value="speech-01-turbo">speech-01-turbo</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 font-bold">API Key</label>
              <input
                type="password"
                value={localApiKey}
                onChange={(e) => setLocalApiKey(e.target.value)}
                onBlur={() =>
                  setSettings((s) => ({ ...s, minimaxApiKey: localApiKey }))
                }
                placeholder="ey..."
                className="w-full p-2 border rounded-lg font-mono text-sm mt-1"
              />
            </div>
            <button
              onClick={() => playAudio("MiniMax is working.")}
              className="w-full py-3 bg-orange-600 text-white font-bold rounded-xl shadow-md active:scale-95 flex items-center justify-center gap-2 mt-2"
            >
              <PlayCircle size={18} /> 测试语音
            </button>
          </div>
        )}
      </div>

      <button
        onClick={() => {
          if (window.confirm("重置所有进度？"))
            setCards((p) =>
              p.map((c) => ({
                ...c,
                status: "new",
                interval: 0,
                repetition: 0,
                wrongCount: 0,
                nextReview: Date.now(),
                lastReview: Date.now(),
              }))
            );
        }}
        className="w-full py-3 bg-red-50 text-red-600 font-bold rounded-xl border border-red-100"
      >
        <AlertTriangle size={16} className="inline mr-1" /> 重置所有进度
      </button>

      {showExport && (
        <SentenceExportModal
          cards={cards}
          settings={settings}
          onClose={() => setShowExport(false)}
        />
      )}
    </div>
  );
};

const StudyView = ({
  cards,
  sessionComplete,
  studyQueue,
  currentIndex,
  sessionType,
  settings,
  setSettings,
  isFlipped,
  setIsFlipped,
  setActiveTab,
  playAudio,
  isPlaying,
  toggleFavorite,
  onHandwritingError,
  handleInlineSave,
  onPrev,
  onGrade,
  onSkip,
  onRestartSession,
  onNext,
}) => {
  const [editMode, setEditMode] = useState(false);
  const [revealChinese, setRevealChinese] = useState(false);
  const progressPercent =
    studyQueue.length > 0 ? ((currentIndex + 1) / studyQueue.length) * 100 : 0;

  useEffect(() => {
    setRevealChinese(false);
  }, [currentIndex]);

  if (sessionComplete)
    return (
      <div className="h-full flex flex-col items-center justify-center p-8 text-center space-y-6 animate-in zoom-in-95">
        <div className="w-24 h-24 bg-green-100 text-green-600 rounded-full flex items-center justify-center shadow-inner">
          <Check size={48} strokeWidth={3} />
        </div>
        <h2 className="text-2xl font-bold text-gray-800">Session Complete!</h2>
        <p className="text-gray-500">所有学习内容已完成</p>
        <button
          onClick={onRestartSession}
          className="px-8 py-3 bg-blue-600 text-white rounded-full font-bold shadow-lg flex items-center gap-2"
        >
          <RotateCcw size={18} /> 重复本组 (Repeat)
        </button>
        <button
          onClick={() => setActiveTab("dashboard")}
          className="text-gray-400 text-sm"
        >
          返回主页
        </button>
      </div>
    );

  const rawCard = studyQueue[currentIndex];
  if (!rawCard) return <div className="p-10 text-center">加载中...</div>;

  const card = cards.find((c) => c.id === rawCard.id) || rawCard;

  const showHandwriting =
    !isFlipped &&
    sessionType !== "speaking" &&
    (settings.hideEnglish || settings.swapSides);

  const renderCardContent = () => {
    if (sessionType === "speaking")
      return (
        <div className="flex-1 w-full px-4 flex flex-col items-center justify-center gap-12 relative">
          <div className="text-center space-y-8 w-full flex flex-col items-center">
            <div
              className={`${
                settings.hideEnglish ? "blur-md select-none opacity-50" : ""
              }`}
            >
              <ClickableWord
                text={settings.swapSides ? card.back : card.front}
                className="text-3xl font-bold text-gray-800 leading-snug"
              />
            </div>
            <div
              onClick={() => setRevealChinese(!revealChinese)}
              className={`cursor-pointer p-4 rounded-xl border w-full max-w-xs transition-all ${
                revealChinese
                  ? "bg-white border-gray-200"
                  : "bg-gray-100 border-dashed border-gray-300"
              }`}
            >
              <div
                className={`text-lg font-medium text-gray-600 transition-all text-center ${
                  revealChinese ? "opacity-100 blur-0" : "opacity-40 blur-sm"
                }`}
              >
                {settings.swapSides ? card.front : card.back}
              </div>
              {!revealChinese && (
                <div className="text-[10px] text-gray-400 mt-1 uppercase tracking-widest text-center">
                  点击显示
                </div>
              )}
            </div>
          </div>
          <button
            onClick={() => playAudio(card.front)}
            className={`w-32 h-32 rounded-full flex flex-col items-center justify-center gap-2 shadow-xl border-4 transition-all ${
              isPlaying
                ? "bg-blue-400 border-blue-200 text-white animate-pulse"
                : "bg-blue-600 border-blue-100 text-white"
            }`}
          >
            {isPlaying ? (
              <Square size={40} fill="currentColor" />
            ) : (
              <Volume2 size={56} />
            )}
          </button>
        </div>
      );

    return (
      <div className="flex-1 w-full flex items-center justify-center px-4 py-4 overflow-y-auto">
        <div className="bg-white w-full rounded-3xl shadow-xl border p-6 flex flex-col items-center justify-center text-center relative overflow-hidden min-h-[50%]">
          {!isFlipped ? (
            <div className="flex flex-col items-center space-y-6 w-full animate-in fade-in">
              {showHandwriting ? (
                <HandwritingWidget
                  targetText={card.front}
                  chineseText={card.back}
                  cardId={card.id}
                  playAudio={playAudio}
                  onNext={() => onGrade("easy")}
                  onError={() => onHandwritingError(card.id)}
                  autoFocus={true}
                />
              ) : (
                <>
                  <ClickableWord
                    text={settings.swapSides ? card.back : card.front}
                    className="text-3xl font-bold text-gray-800"
                  />
                  <button
                    onClick={() => playAudio(card.front)}
                    className="p-4 bg-blue-50 text-blue-600 rounded-full mt-4"
                  >
                    <Volume2 size={24} />
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="w-full space-y-6 animate-in flip-in-y">
              <div>
                <div className="text-xs font-bold text-blue-500 uppercase mb-1">
                  English
                </div>
                <ClickableWord
                  text={card.front}
                  className="text-2xl font-bold text-gray-800"
                />
              </div>
              <hr />
              <div>
                <div className="text-xs font-bold text-gray-400 uppercase mb-1">
                  Chinese
                </div>
                <div className="text-xl text-gray-600">{card.back}</div>
              </div>
              <button
                onClick={() => playAudio(card.front)}
                className="p-3 bg-gray-100 rounded-full"
              >
                <Volume2 size={24} />
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full bg-gray-50 overflow-hidden relative">
      <div className="flex-none h-16 bg-white border-b px-4 flex items-center justify-between z-50 relative shadow-sm">
        <span className="font-mono text-gray-500 font-bold text-sm">
          {currentIndex + 1} / {studyQueue.length}
        </span>
        <div className="flex gap-1 items-center">
          <button onClick={onSkip} className="p-2 text-gray-400">
            <Ban size={20} />
          </button>
          <button
            onClick={() =>
              setSettings((s) => ({ ...s, swapSides: !s.swapSides }))
            }
            className={`p-2 rounded-full ${
              settings.swapSides
                ? "bg-indigo-100 text-indigo-600"
                : "text-gray-400"
            }`}
          >
            <ArrowRightLeft size={18} />
          </button>
          <button
            onClick={() => setEditMode(true)}
            className="p-2 text-gray-400"
          >
            <Edit size={20} />
          </button>
          <button
            onClick={() =>
              setSettings((s) => ({ ...s, hideEnglish: !s.hideEnglish }))
            }
            className={`p-2 rounded-full ${
              settings.hideEnglish
                ? "bg-purple-100 text-purple-600"
                : "text-gray-400"
            }`}
          >
            {settings.hideEnglish ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
          <button
            type="button"
            onClick={(e) =>
              toggleFavorite(
                card.id,
                e,
                sessionType === "speaking" ? "listening" : "general"
              )
            }
            className={`p-2 rounded-full transition-colors ${
              (
                sessionType === "speaking"
                  ? card.isListeningFavorite
                  : card.isFavorite
              )
                ? "text-yellow-500 bg-yellow-50"
                : "text-gray-400"
            }`}
          >
            <Star
              size={20}
              fill={
                (
                  sessionType === "speaking"
                    ? card.isListeningFavorite
                    : card.isFavorite
                )
                  ? "currentColor"
                  : "none"
              }
            />
          </button>
        </div>
        <div className="absolute bottom-0 left-0 w-full h-1 bg-gray-100">
          <div
            className="h-full bg-blue-600 transition-all rounded-r-full"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>
      <div className="flex-1 flex flex-col justify-center relative overflow-hidden w-full">
        {renderCardContent()}
      </div>
      <div className="flex-none h-24 px-4 flex items-center justify-center bg-gray-50 z-40 border-t">
        {!isFlipped && sessionType !== "speaking" ? (
          <button
            onClick={() => setIsFlipped(true)}
            className="w-full bg-gray-900 text-white py-4 rounded-2xl font-bold text-lg"
          >
            显示答案
          </button>
        ) : (
          <div className="flex gap-2 w-full">
            <button
              onClick={onPrev}
              disabled={currentIndex === 0}
              className="p-4 bg-gray-200 rounded-xl text-gray-600 disabled:opacity-30"
            >
              <ChevronLeft size={24} />
            </button>
            {sessionType !== "speaking" ? (
              <div className="flex-1 grid grid-cols-3 gap-2">
                <button
                  onClick={() => onGrade("hard")}
                  className="bg-red-100 text-red-600 rounded-xl font-bold text-sm"
                >
                  困难
                </button>
                <button
                  onClick={() => onGrade("medium")}
                  className="bg-yellow-100 text-yellow-700 rounded-xl font-bold text-sm"
                >
                  一般
                </button>
                <button
                  onClick={() => onGrade("easy")}
                  className="bg-green-100 text-green-700 rounded-xl font-bold text-sm"
                >
                  简单
                </button>
              </div>
            ) : (
              <button
                onClick={() => onGrade("easy")}
                className="flex-1 py-4 bg-gray-900 text-white rounded-xl font-bold"
              >
                {currentIndex === studyQueue.length - 1 ? "完成" : "下一个"}
              </button>
            )}
          </div>
        )}
      </div>
      {editMode && (
        <EditModal
          card={card}
          settings={settings}
          onClose={() => setEditMode(false)}
          onSave={handleInlineSave}
        />
      )}
    </div>
  );
};

export default function App() {
  useEffect(() => {
    if (!document.querySelector("#tailwind-script")) {
      const script = document.createElement("script");
      script.src = "https://cdn.tailwindcss.com";
      script.id = "tailwind-script";
      script.async = true;
      document.head.appendChild(script);
    }
    const addMeta = (n, c) => {
      let m = document.querySelector(`meta[name="${n}"]`);
      if (!m) {
        m = document.createElement("meta");
        m.name = n;
        document.head.appendChild(m);
      }
      m.content = c;
    };
    addMeta("apple-mobile-web-app-capable", "yes");
    addMeta("apple-mobile-web-app-status-bar-style", "black-translucent");
    addMeta(
      "viewport",
      "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover"
    );
    const s = document.createElement("style");
    s.innerHTML =
      "body{overscroll-behavior-y:none;touch-action:none;-webkit-tap-highlight-color:transparent;-webkit-text-size-adjust:100%}.overflow-y-auto{touch-action:pan-y}.no-scrollbar::-webkit-scrollbar{display:none}.no-scrollbar{-ms-overflow-style:none;scrollbar-width:none}";
    document.head.appendChild(s);
  }, []);

  const [cards, setCards] = useState(() => {
    try {
      const s = localStorage.getItem("snapSent_cards_v8");
      return s ? JSON.parse(s) : INITIAL_CARDS;
    } catch (e) {
      return INITIAL_CARDS;
    }
  });

  const [settings, setSettings] = useState(() => {
    try {
      const s = localStorage.getItem("snapSent_settings_v10");
      const d = {
        accessPassword: "811100", // --- 修改点：设定默认密码 ---
        autoPlayAudio: true,
        swapSides: false,
        hideEnglish: false,
        playbackRate: 1.0,
        reviewLimit: 20,
        browseGroupSize: 50,
        categories: ["默认"],
        lastBackupDate: null,
        ttsProvider: "browser",
        // --- 修改点：在此加入默认接口配置供初始化 ---
        minimaxBaseUrl: "https://api.minimax.chat/v1/t2a_v2",
        elevenLabsBaseUrl: "https://api.elevenlabs.io/v1/text-to-speech",
        googleBaseUrl: "https://texttospeech.googleapis.com/v1/text:synthesize",
      };

      const parsed = s ? JSON.parse(s) : {};
      // 强制更新已有用户的密码为 811100
      parsed.accessPassword = "811100";

      return { ...d, ...parsed, accessPassword: "811100" };
    } catch (e) {
      return {
        accessPassword: "811100",
        categories: ["默认"],
        ttsProvider: "browser",
        minimaxBaseUrl: "https://api.minimax.chat/v1/t2a_v2",
        elevenLabsBaseUrl: "https://api.elevenlabs.io/v1/text-to-speech",
        googleBaseUrl: "https://texttospeech.googleapis.com/v1/text:synthesize",
      };
    }
  });

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [inputPwd, setInputPwd] = useState("");
  const [loginError, setLoginError] = useState(false);
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [studyQueue, setStudyQueue] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [sessionType, setSessionType] = useState("review");
  const [sessionComplete, setSessionComplete] = useState(false);
  const [selectionTarget, setSelectionTarget] = useState(null);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [browseMode, setBrowseMode] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasSavedSession, setHasSavedSession] = useState(false);
  const synth = useRef(window.speechSynthesis);
  const audioRef = useRef(null);

  useEffect(() => {
    localStorage.setItem("snapSent_cards_v8", JSON.stringify(cards));
  }, [cards]);
  useEffect(() => {
    localStorage.setItem("snapSent_settings_v10", JSON.stringify(settings));
  }, [settings]);

  useEffect(() => {
    const saved = localStorage.getItem("snapSent_session");
    if (saved) setHasSavedSession(true);
  }, []);

  useEffect(() => {
    if (studyQueue.length > 0 && !sessionComplete && activeTab === "study") {
      localStorage.setItem(
        "snapSent_session",
        JSON.stringify({
          queue: studyQueue,
          index: currentIndex,
          type: sessionType,
        })
      );
      setHasSavedSession(true);
    } else if (
      sessionComplete ||
      (activeTab === "dashboard" && studyQueue.length === 0)
    ) {
      if (sessionComplete) {
        localStorage.removeItem("snapSent_session");
        setHasSavedSession(false);
      }
    }
  }, [studyQueue, currentIndex, sessionComplete, sessionType, activeTab]);

  const resumeSession = () => {
    try {
      const saved = localStorage.getItem("snapSent_session");
      if (saved) {
        const { queue, index, type } = JSON.parse(saved);
        setStudyQueue(queue);
        setCurrentIndex(index);
        setSessionType(type);
        setSessionComplete(false);
        setActiveTab("study");
      }
    } catch (e) {
      console.error("Failed to resume session");
    }
  };

  const handleLogin = () => {
    if (inputPwd === settings.accessPassword) {
      setIsUnlocking(true);
      setTimeout(() => {
        setIsAuthenticated(true);
        sessionStorage.setItem("snapSent_auth", "true");
        setIsUnlocking(false);
      }, 500);
    } else {
      setLoginError(true);
      setInputPwd("");
    }
  };

  const playAudio = async (t) => {
    if (!t) return;
    if (isPlaying) {
      synth.current.cancel();
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    }
    setIsPlaying(true);
    try {
      if (settings.ttsProvider === "browser") {
        const u = new SpeechSynthesisUtterance(t);
        u.rate = settings.playbackRate;
        u.onend = () => setIsPlaying(false);
        u.onerror = () => setIsPlaying(false);
        synth.current.speak(u);
      } else if (settings.ttsProvider === "minimax" && settings.minimaxApiKey) {
        // --- 修改点：读取动态 BaseUrl ---
        const baseUrl =
          settings.minimaxBaseUrl || "https://api.minimax.chat/v1/t2a_v2";
        const res = await fetch(baseUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${settings.minimaxApiKey}`,
          },
          body: JSON.stringify({
            model: settings.minimaxModel || "speech-02-hd",
            text: t,
            voice_setting: {
              voice_id: "male-qn-qingse",
              speed: settings.playbackRate,
            },
          }),
        });
        const d = await res.json();
        if (d.data?.audio) {
          const typedArray = new Uint8Array(
            d.data.audio.match(/.{1,2}/g).map((byte) => parseInt(byte, 16))
          );
          const blob = new Blob([typedArray], { type: "audio/mpeg" });
          const url = URL.createObjectURL(blob);
          const a = new Audio();
          a.src = url;
          a.crossOrigin = "anonymous";
          audioRef.current = a;
          a.onended = () => {
            setIsPlaying(false);
            URL.revokeObjectURL(url);
          };
          a.onerror = () => {
            setIsPlaying(false);
            URL.revokeObjectURL(url);
          };
          await a.play();
        } else {
          setIsPlaying(false);
        }
      } else if (settings.ttsProvider === "openai" && settings.openAIKey) {
        let baseUrl = settings.openAIBaseUrl || "https://api.openai.com/v1";
        if (baseUrl.endsWith("/")) baseUrl = baseUrl.slice(0, -1);
        if (!baseUrl.includes("/v1")) baseUrl = `${baseUrl}/v1`;
        const res = await fetch(`${baseUrl}/audio/speech`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${settings.openAIKey}`,
          },
          body: JSON.stringify({
            model: "tts-1",
            input: t,
            voice: "alloy",
            speed: settings.playbackRate,
          }),
        });
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = new Audio(url);
        a.crossOrigin = "anonymous";
        audioRef.current = a;
        a.onended = () => {
          setIsPlaying(false);
          URL.revokeObjectURL(url);
        };
        a.onerror = () => {
          setIsPlaying(false);
          URL.revokeObjectURL(url);
        };
        await a.play();
      } else if (
        settings.ttsProvider === "elevenlabs" &&
        settings.elevenLabsApiKey
      ) {
        // --- 修改点：彻底补全 ElevenLabs API 播放逻辑 ---
        const baseUrl =
          settings.elevenLabsBaseUrl ||
          "https://api.elevenlabs.io/v1/text-to-speech";
        const voiceId = settings.elevenLabsVoiceId || "21m00Tcm4TlvDq8ikWAM";
        const url = baseUrl.endsWith(voiceId)
          ? baseUrl
          : `${baseUrl.replace(/\/$/, "")}/${voiceId}`;

        const res = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "xi-api-key": settings.elevenLabsApiKey,
          },
          body: JSON.stringify({
            text: t,
            model_id: "eleven_monolingual_v1",
            voice_settings: {
              stability: 0.5,
              similarity_boost: 0.5,
            },
          }),
        });
        const blob = await res.blob();
        const audioUrl = URL.createObjectURL(blob);
        const a = new Audio(audioUrl);
        audioRef.current = a;
        a.onended = () => {
          setIsPlaying(false);
          URL.revokeObjectURL(audioUrl);
        };
        a.onerror = () => {
          setIsPlaying(false);
          URL.revokeObjectURL(audioUrl);
        };
        await a.play();
      } else if (settings.ttsProvider === "google" && settings.googleApiKey) {
        // --- 修改点：彻底补全 Google TTS API 播放逻辑 ---
        const baseUrl =
          settings.googleBaseUrl ||
          "https://texttospeech.googleapis.com/v1/text:synthesize";
        const res = await fetch(`${baseUrl}?key=${settings.googleApiKey}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            input: { text: t },
            voice: { languageCode: "en-US", name: "en-US-Neural2-F" },
            audioConfig: { audioEncoding: "MP3" },
          }),
        });
        const d = await res.json();
        if (d.audioContent) {
          const audioUrl = `data:audio/mp3;base64,${d.audioContent}`;
          const a = new Audio(audioUrl);
          audioRef.current = a;
          a.onended = () => setIsPlaying(false);
          a.onerror = () => setIsPlaying(false);
          await a.play();
        } else {
          setIsPlaying(false);
        }
      } else {
        setIsPlaying(false);
      }
    } catch (e) {
      console.error("Audio playback error:", e);
      setIsPlaying(false);
    }
  };

  const handleHandwritingError = (id) => {
    setCards((prev) =>
      prev.map((c) =>
        c.id === id
          ? {
              ...c,
              isFavorite: true,
              isListeningFavorite: true,
              wrongCount: (c.wrongCount || 0) + 1,
              nextReview: Date.now(),
              lastReview: Date.now(),
            }
          : c
      )
    );
  };

  const toggleFavorite = (id, e, type = "general") => {
    if (e) e.stopPropagation();
    setCards((prev) =>
      prev.map((c) => {
        if (c.id !== id) return c;
        if (type === "listening")
          return {
            ...c,
            isListeningFavorite: !c.isListeningFavorite,
            isFavorite: true,
            nextReview: !c.isListeningFavorite ? Date.now() : c.nextReview,
          };
        return { ...c, isFavorite: !c.isFavorite };
      })
    );
  };

  const handleInlineSave = (id, f, b) => {
    const updater = (prev) =>
      prev.map((c) => (c.id === id ? { ...c, front: f, back: b } : c));
    setCards(updater);
    setStudyQueue(updater);
  };

  const startSession = (q, t) => {
    if (q.length === 0) return alert("暂无内容");
    setSelectionTarget(null);
    setSessionType(t);
    setStudyQueue([...q].sort(() => Math.random() - 0.5));
    setCurrentIndex(0);
    setIsFlipped(false);
    setSessionComplete(false);
    setActiveTab("study");
  };

  const handleGrade = (r) => {
    const card = studyQueue[currentIndex];
    let { interval, repetition, ef, wrongCount = 0 } = card;
    const q = r === "easy" ? 5 : r === "medium" ? 4 : 3;

    if (q >= 4) {
      wrongCount = Math.max(0, wrongCount - 1);
      if (repetition === 0) interval = 1;
      else if (repetition === 1) interval = 3;
      else if (repetition === 2) interval = 7;
      else interval = Math.round(interval * ef);
      repetition += 1;
    } else {
      wrongCount += 1;
      repetition = 0;
      interval = 1;
    }

    if (r === "hard" || wrongCount > 0) {
      interval = 0.5;
    }
    if (card.isFavorite && r === "easy" && interval > 3) {
      interval = 3;
    }

    ef = Math.max(1.3, ef + (0.1 - (5 - q) * (0.08 + (5 - q) * 0.02)));

    let next;
    if (r === "easy" && wrongCount === 0) {
      next = Date.now() + interval * 86400000;
    } else {
      const shortGap = interval < 1 ? 12 * 3600 * 1000 : 24 * 3600 * 1000;
      next = Date.now() + shortGap;
    }

    const updated = {
      ...card,
      interval,
      repetition,
      ef,
      wrongCount,
      lastReview: Date.now(),
      nextReview: next,
      status: repetition > 6 ? "mastered" : "review",
    };

    setCards((prev) => prev.map((c) => (c.id === card.id ? updated : c)));

    let nq = [...studyQueue];
    if (r !== "easy") {
      const insertAt = currentIndex + (r === "hard" ? 3 : 7);
      nq.splice(insertAt, 0, { ...updated, _isRepeat: true });
    }
    setStudyQueue(nq);

    if (currentIndex < nq.length - 1) {
      setCurrentIndex((p) => p + 1);
      setIsFlipped(false);
    } else {
      setSessionComplete(true);
    }
  };

  if (!isAuthenticated)
    return (
      <LoginView
        inputPwd={inputPwd}
        setInputPwd={setInputPwd}
        handleLogin={handleLogin}
        isUnlocking={isUnlocking}
        loginError={loginError}
      />
    );

  return (
    <div className="min-h-screen bg-gray-50 pb-safe">
      <header className="bg-white border-b sticky top-0 z-30 px-4 py-3 flex items-center justify-between h-14 shadow-sm">
        <div
          onClick={() => {
            setActiveTab("dashboard");
            setBrowseMode(null);
          }}
          className="flex items-center gap-2 font-bold text-xl text-blue-600 cursor-pointer"
        >
          <div className="w-8 h-8 bg-blue-600 text-white rounded-lg flex items-center justify-center">
            S
          </div>
          SnapSent
        </div>
        {activeTab === "study" && (
          <button onClick={() => setActiveTab("dashboard")}>
            <X className="text-gray-400" />
          </button>
        )}
      </header>

      <TextSelectionHandler />

      <main className="max-w-md mx-auto h-[calc(100dvh-56px)] overflow-hidden">
        {activeTab === "dashboard" && !selectionTarget && (
          <DashboardView
            cards={cards}
            startSession={startSession}
            setSelectionTarget={setSelectionTarget}
            setActiveTab={setActiveTab}
            hasSavedSession={hasSavedSession}
            resumeSession={resumeSession}
          />
        )}
        {selectionTarget && (
          <SelectionView
            cards={cards}
            settings={settings}
            selectionTarget={selectionTarget}
            setSelectionTarget={setSelectionTarget}
            startSession={startSession}
          />
        )}
        {activeTab === "browse" && (
          <BrowseListView
            browseMode={browseMode}
            setBrowseMode={setBrowseMode}
            setActiveTab={setActiveTab}
            cards={cards}
            onSave={handleInlineSave}
            onDelete={(id) =>
              setCards((p) =>
                p.map((c) => (c.id === id ? { ...c, status: "deleted" } : c))
              )
            }
            onToggleFavorite={toggleFavorite}
            onHandwritingError={handleHandwritingError}
            playAudio={playAudio}
            isPlaying={isPlaying}
            settings={settings}
          />
        )}
        {activeTab === "study" && (
          <StudyView
            cards={cards}
            sessionComplete={sessionComplete}
            studyQueue={studyQueue}
            currentIndex={currentIndex}
            sessionType={sessionType}
            settings={settings}
            setSettings={setSettings}
            isFlipped={isFlipped}
            setIsFlipped={setIsFlipped}
            setActiveTab={setActiveTab}
            playAudio={playAudio}
            isPlaying={isPlaying}
            toggleFavorite={toggleFavorite}
            onHandwritingError={handleHandwritingError}
            handleInlineSave={handleInlineSave}
            onPrev={() => currentIndex > 0 && setCurrentIndex((p) => p - 1)}
            onGrade={handleGrade}
            onSkip={() => {
              const nq = studyQueue.filter((_, i) => i !== currentIndex);
              setStudyQueue(nq);
              if (currentIndex >= nq.length) setSessionComplete(true);
            }}
            onRestartSession={() => startSession(studyQueue, sessionType)}
            onNext={() =>
              currentIndex < studyQueue.length - 1
                ? setCurrentIndex((p) => p + 1)
                : setSessionComplete(true)
            }
          />
        )}
        {activeTab === "add" && (
          <AddCardView
            settings={settings}
            setSettings={setSettings}
            cards={cards}
            setCards={setCards}
            onInlineSave={handleInlineSave}
          />
        )}
        {activeTab === "settings" && (
          <SettingsView
            settings={settings}
            setSettings={setSettings}
            cards={cards}
            setCards={setCards}
            playAudio={playAudio}
          />
        )}
      </main>

      {!["study"].includes(activeTab) && !selectionTarget && (
        <nav className="fixed bottom-0 w-full bg-white border-t h-16 pb-2 z-40 max-w-md mx-auto left-0 right-0">
          <div className="grid grid-cols-3 h-full">
            <button
              onClick={() => {
                setActiveTab("dashboard");
                setBrowseMode(null);
              }}
              className={`flex flex-col items-center justify-center gap-1 ${
                activeTab === "dashboard" ? "text-blue-600" : "text-gray-400"
              }`}
            >
              <Home size={24} />
              <span className="text-[10px]">主页</span>
            </button>
            <button
              onClick={() => {
                setActiveTab("add");
                setBrowseMode(null);
              }}
              className={`flex flex-col items-center justify-center gap-1 ${
                activeTab === "add" ? "text-blue-600" : "text-gray-400"
              }`}
            >
              <FolderPlus size={24} />
              <span className="text-[10px]">添加</span>
            </button>
            <button
              onClick={() => {
                setActiveTab("settings");
                setBrowseMode(null);
              }}
              className={`flex flex-col items-center justify-center gap-1 ${
                activeTab === "settings" ? "text-blue-600" : "text-gray-400"
              }`}
            >
              <Settings size={24} />
              <span className="text-[10px]">设置</span>
            </button>
          </div>
        </nav>
      )}
    </div>
  );
}
